/**
 * Boot Scene: generates all procedural textures used throughout the game.
 *
 * Since the game uses a fully monochrome, minimalist aesthetic, every
 * visual asset is drawn programmatically with Phaser Graphics — no
 * external image files are needed.
 *
 * After texture generation, transitions to the TitleScene.
 */

import Phaser from 'phaser';
import {
  SCENE_BOOT,
  SCENE_TITLE,
  TEX_PLAYER,
  TEX_WALL,
  TEX_WALL_BRICK,
  TEX_WALL_STONE,
  TEX_WALL_WOOD,
  TEX_WALL_METAL,
  TEX_WALL_LATTICE,
  TEX_WALL_RUINS,
  TEX_WALL_MARBLE,
  TEX_WALL_ADOBE,
  TEX_FOOD_FRUIT,
  TEX_FOOD_CHICKEN,
  TEX_FOOD_TUNA,
  TEX_COIN,
  TEX_LIFE_ICON,
  TEX_EXIT,
  TEX_DUST,
  TEX_GLOW,
  TEX_WATER,
  TEX_LAVA,
  TEX_SMOKE,
  TEX_LAVA_RIM,
  TEX_POISON,
  TEX_POISON_RIM,
  TEX_SPIKE,
  TEX_SPIKE_RIM,
  TEX_BLOOD,
  TEX_KEY_METALLIC,
  TEX_KEY_COPPER,
  TEX_KEY_GOLD,
  TEX_CHEST_METALLIC,
  TEX_CHEST_COPPER,
  TEX_CHEST_GOLD,
  TEX_LOOT_HEART,
  TEX_LOOT_STAR,
  TEX_LOOT_AGMUTH,
  TEX_LOOT_COIN_BAG,
  TEX_COMPASS,
  TEX_COMPASS_NEEDLE,
  TEX_THREIA_TOTEM,
  TEX_THREIA_GLOW,
  TEX_SHOP_THREIAS_GUIDE,
  TEX_SHOP_HEART_POTION,
  TEX_SHOP_TOTEM_VORACIOUS,
  TEX_SHOP_AGMUTH_PENDANT,
  TEX_SHOP_LAVA_RING,
  TEX_SHOP_POISON_RING,
  TEX_SHOP_WATER_RING,
  TEX_SHOP_KEY_PENDANT,
  TEX_SHOP_HEART_PENDANT,
  TEX_SHOP_ELEMENTAL_RING,
  TEX_SHOP_TEAR_OF_THREIA,
  TEX_HAMMER_OF_THREIA,
  TEX_WALL_DEBRIS,
  TEX_SHOP_HAMMER_OF_THREIA,
  TEX_BAT_WINGS_UP,
  TEX_BAT_WINGS_DOWN,
  TEX_MANUSCRIPT,
  TEX_GOLDEN_EXIT,
  TEX_GOLDEN_EXIT_SPENT,
  TEX_CELESTIAL_EXIT,
  TEX_VORATH_MASS,
  TEX_ALTAR_THREIA,
  TEX_DARK_SPHERE,
  TEX_BEAM_PARTICLE,
  // New feature textures (medium-priority features)
  TEX_SECRET_WALL,
  TEX_SHADOW_WALKER,
  TEX_BAT_NEST,
  TEX_BAT_NEST_DESTROYED,
  TEX_WALL_CRUMBLED,
} from '../utils/constants.js';
import {
  TILE_SIZE,
  PLAYER_SIZE,
  COLOR_WHITE,
  COLOR_WALL_MORTAR,
  COLOR_WALL_BRICK_FACE,
  COLOR_WALL_STONE_LIGHT,
  COLOR_WALL_STONE_MID,
  COLOR_WALL_STONE_SEAM,
  COLOR_WALL_WOOD_PLANK,
  COLOR_WALL_WOOD_GRAIN,
  COLOR_WALL_WOOD_GAP,
  COLOR_WALL_METAL_PLATE,
  COLOR_WALL_METAL_DARK,
  COLOR_WALL_METAL_RIVET,
  COLOR_WALL_METAL_SEAM,
  COLOR_WALL_LATTICE_BAR,
  COLOR_WALL_LATTICE_BG,
  COLOR_WALL_LATTICE_HIGHLIGHT,
  COLOR_WALL_RUINS_BLOCK,
  COLOR_WALL_RUINS_CRACK,
  COLOR_WALL_RUINS_SHADOW,
  COLOR_WALL_MARBLE_BASE,
  COLOR_WALL_MARBLE_VEIN,
  COLOR_WALL_MARBLE_VEIN2,
  COLOR_WALL_ADOBE_BASE,
  COLOR_WALL_ADOBE_CRACK,
  COLOR_WALL_ADOBE_SHADOW,
  COLOR_METALLIC,
  COLOR_METALLIC_DARK,
  COLOR_COPPER,
  COLOR_COPPER_DARK,
  COLOR_GOLD,
  COLOR_GOLD_DARK,
  AUDIO_TITLE_BG,
  AUDIO_COIN,
  AUDIO_FOOD_FRUIT,
  AUDIO_FOOD_CHICKEN,
  AUDIO_FOOD_TUNA,
  AUDIO_BALL_MOVING,
  AUDIO_GAME_BG,
  AUDIO_GAME_BG_COUNT,
  AUDIO_LEVEL_COMPLETE,
  AUDIO_LEVEL_NEW,
  AUDIO_HEART_LOSS,
  AUDIO_WATER_WALK,
  AUDIO_GAME_END,
  AUDIO_GAME_END_RESULTS,
  AUDIO_MENU_CLICK,
  AUDIO_ESC_PRESSED,
  AUDIO_MENU_SELECT,
  AUDIO_RECOVER_LIFE,
  AUDIO_LAVA_WALK,
  AUDIO_SPIKE_WALK,
  AUDIO_POISON_WALK,
  AUDIO_KEY_COLLECT,
  AUDIO_CHEST_OPEN,
  AUDIO_CHEST_LOCKED,
  AUDIO_HEART_COLLECT,
  AUDIO_STAR_COLLECT,
  AUDIO_AGMUTH_COLLECT,
  AUDIO_AGMUTH_USED,
  AUDIO_COIN_BAG_COLLECT,
  AUDIO_COMPASS_COLLECT,
  AUDIO_THREIA_TOTEM_COLLECT,
  AUDIO_THREIA_PRAYER_HUM,
  AUDIO_THREIA_BLESSING,
  AUDIO_THREIA_FAIL,
  AUDIO_SHOP_BUY,
  AUDIO_SHOP_DENY,
  AUDIO_HEART_POTION_USE,
  AUDIO_TEAR_OF_THREIA_USE,
  COLOR_POTION_GREEN,
  COLOR_LAVA_RING,
  COLOR_POISON_RING,
  COLOR_WATER_RING,
  COLOR_PENDANT_GEM,
  COLOR_KEY_PENDANT,
  COLOR_VORACIOUS_VIOLET,
  COLOR_GUIDE_PARCHMENT,
  COLOR_HEART_PENDANT,
  COLOR_ELEMENTAL_RING,
  COLOR_TEAR_THREIA_BLUE,
  COLOR_TEAR_THREIA_GOLD,
  AUDIO_BAT_SCREECH,
  AUDIO_BAT_HIT,
  AUDIO_BAT_REPEL,
  COLOR_BAT_EYES,
  BAT_SIZE,
  AUDIO_ZOOM_OUT,
  AUDIO_MANUSCRIPT_COLLECT,
  AUDIO_MANUSCRIPT_OPEN,
  COLOR_MANUSCRIPT_PARCHMENT,
  AUDIO_LIBRARY_BG,
  AUDIO_HAMMER_COLLECT,
  AUDIO_HAMMER_HIT,
  AUDIO_HAMMER_BREAK,
  AUDIO_WALL_REGEN,
  COLOR_HAMMER_BLUE,
  COLOR_HAMMER_GOLD,
  AUDIO_NARRATOR_MONK,
  AUDIO_NARRATOR_VORATH,
  COLOR_GOLDEN_EXIT,
  COLOR_GOLDEN_EXIT_DARK,
  AUDIO_TREASURE_BG,
  AUDIO_TREASURE_TICK,
  AUDIO_TREASURE_ENTER,
  AUDIO_TREASURE_EXIT,
  TREASURE_ROOM_MUSIC_COUNT,
  // Final Battle audio & colors
  AUDIO_CELESTIAL_MATERIALIZE,
  AUDIO_CELESTIAL_HUM,
  AUDIO_FINAL_ENERGY,
  AUDIO_THREIA_VOICE,
  AUDIO_THREIA_MUSIC,
  AUDIO_VORATH_MACABRE,
  AUDIO_VORATH_ARENA_MUSIC,
  AUDIO_VORATH_SPHERE_LAUNCH,
  AUDIO_VORATH_SPHERE_HIT,
  AUDIO_VORATH_SPHERE_WALL,
  AUDIO_ALTAR_ACTIVATE,
  AUDIO_ALTAR_BEAM,
  AUDIO_VORATH_DESTROY,
  AUDIO_FINAL_CELESTIAL,
  AUDIO_FINAL_MUSIC,
  AUDIO_FINAL_TRANSITION,
  COLOR_CELESTIAL_EXIT,
  COLOR_CELESTIAL_GOLD,
  COLOR_CELESTIAL_ORANGE,
  COLOR_VORATH_DARK,
  COLOR_VORATH_RED,
  COLOR_VORATH_ORANGE,
  COLOR_ALTAR_GOLD,
  COLOR_ALTAR_CELESTIAL,
  COLOR_DARK_SPHERE,
  COLOR_DARK_SPHERE_RIM,
  FINAL_VORATH_SIZE,
  FINAL_ALTAR_SIZE,
  FINAL_VORATH_SPHERE_SIZE,
  // Ambient sound keys
  AUDIO_AMBIENT_WATER_DRIP,
  AUDIO_AMBIENT_LAVA_CRACKLE,
  AUDIO_AMBIENT_VORATH_WHISPER,
  AUDIO_AMBIENT_CREAK_1,
  AUDIO_AMBIENT_CREAK_2,
  AUDIO_AMBIENT_WIND,
  // Moving Wall constants
  // Moving Wall constants
  AUDIO_WALL_SHIFT_WARNING,
  AUDIO_WALL_SHIFT,
  COLOR_MOVING_WALL_GLOW,
  // Secret Passage constants
  AUDIO_SECRET_PASSAGE_OPEN,
  AUDIO_SECRET_PASSAGE_HINT,
  COLOR_SECRET_WALL_CRACK,
  // Shadow Walker constants
  SHADOW_WALKER_SIZE,
  AUDIO_SHADOW_WALKER_SPOT,
  AUDIO_SHADOW_WALKER_HIT,
  AUDIO_SHADOW_WALKER_DESPAWN,
  COLOR_SHADOW_WALKER,
  COLOR_SHADOW_WALKER_EYES,
  // Bat Nest constants
  BAT_NEST_SIZE,
  AUDIO_BAT_NEST_ACTIVATE,
  AUDIO_BAT_NEST_DESTROY,
  COLOR_BAT_NEST,
  COLOR_BAT_NEST_EYES,
  COLOR_BAT_NEST_CRUMBLED,
} from '../config/game-config.js';

export default class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENE_BOOT });
  }

  preload() {
    // ── Loading overlay progress ────────────────────────────────
    const barFill = document.getElementById('loading-bar-fill');
    const statusText = document.getElementById('loading-status');

    this.load.on('progress', (value) => {
      if (barFill) barFill.style.width = `${Math.round(value * 100)}%`;
      if (statusText) {
        const pct = Math.round(value * 100);
        statusText.textContent = pct < 100 ? `Loading  ${pct}%` : 'Preparing';
      }
    });

    // Load audio assets
    this.load.audio(AUDIO_TITLE_BG, 'sound/main-title-bg-1.mp3');
    this.load.audio(AUDIO_COIN, 'sound/coin-0.mp3');
    this.load.audio(AUDIO_FOOD_FRUIT, 'sound/meal-fruit-2.mp3');
    this.load.audio(AUDIO_FOOD_CHICKEN, 'sound/meal-chicken-1.mp3');
    this.load.audio(AUDIO_FOOD_TUNA, 'sound/meal-tuna-1.mp3');
    this.load.audio(AUDIO_BALL_MOVING, 'sound/ball-moving-1.mp3');
    // Load all game background music tracks (game-music-bg-1.mp3 … 9.mp3)
    for (let i = 1; i <= AUDIO_GAME_BG_COUNT; i++) {
      this.load.audio(`${AUDIO_GAME_BG}-${i}`, `sound/game-music-bg-${i}.mp3`);
    }
    this.load.audio(AUDIO_LEVEL_COMPLETE, 'sound/level-complete-1.mp3');
    this.load.audio(AUDIO_LEVEL_NEW, 'sound/level-new.mp3');
    this.load.audio(AUDIO_HEART_LOSS, 'sound/heart-loss-1.mp3');
    this.load.audio(AUDIO_WATER_WALK, 'sound/water-walk-1.mp3');
    this.load.audio(AUDIO_GAME_END, 'sound/game-end-1.mp3');
    this.load.audio(AUDIO_GAME_END_RESULTS, 'sound/game-end-2.mp3');
    this.load.audio(AUDIO_MENU_CLICK, 'sound/menu-click-1.mp3');
    this.load.audio(AUDIO_ESC_PRESSED, 'sound/esc-pressed-1.mp3');
    this.load.audio(AUDIO_MENU_SELECT, 'sound/menu-select-1.mp3');
    this.load.audio(AUDIO_RECOVER_LIFE, 'sound/recover-life-gauge.mp3');
    this.load.audio(AUDIO_LAVA_WALK, 'sound/lava-walk-1.mp3');
    this.load.audio(AUDIO_SPIKE_WALK, 'sound/spikes-walk-1.mp3');
    this.load.audio(AUDIO_POISON_WALK, 'sound/poison-walk-1.mp3');
    this.load.audio(AUDIO_ZOOM_OUT, 'sound/zoom-out-1.mp3');
    // Key & Chest audio (wired for future audio files)
    this.load.audio(AUDIO_KEY_COLLECT, 'sound/key-collect-1.mp3');
    this.load.audio(AUDIO_CHEST_OPEN, 'sound/chest-open-1.mp3');
    this.load.audio(AUDIO_CHEST_LOCKED, 'sound/chest-locked-1.mp3');
    this.load.audio(AUDIO_HEART_COLLECT, 'sound/heart-collect-1.mp3');
    this.load.audio(AUDIO_STAR_COLLECT, 'sound/star-collect-1.mp3');
    this.load.audio(AUDIO_AGMUTH_COLLECT, 'sound/agmuth-collect-1.mp3');
    this.load.audio(AUDIO_AGMUTH_USED, 'sound/agmuth-used-normal-1.mp3');
    this.load.audio(AUDIO_COIN_BAG_COLLECT, 'sound/coin-bag-collect-1.mp3');
    this.load.audio(AUDIO_COMPASS_COLLECT, 'sound/compass-collect-1.mp3');
    // Threia audio (wired for future audio files)
    this.load.audio(AUDIO_THREIA_TOTEM_COLLECT, 'sound/threia-totem-collect-1.mp3');
    this.load.audio(AUDIO_THREIA_PRAYER_HUM, 'sound/threia-prayer-hum-1.mp3');
    this.load.audio(AUDIO_THREIA_BLESSING, 'sound/threia-blessing-1.mp3');
    this.load.audio(AUDIO_THREIA_FAIL, 'sound/threia-fail-1.mp3');
    // Shop audio (wired for future audio files)
    this.load.audio(AUDIO_SHOP_BUY, 'sound/shop-buy-1.mp3');
    this.load.audio(AUDIO_SHOP_DENY, 'sound/shop-deny-1.mp3');
    this.load.audio(AUDIO_HEART_POTION_USE, 'sound/heart-potion-use-1.mp3');
    this.load.audio(AUDIO_TEAR_OF_THREIA_USE, 'sound/tear-of-threia-use-1.mp3');
    // Bat audio (wired for future audio files)
    this.load.audio(AUDIO_BAT_SCREECH, 'sound/bat-screech-1.mp3');
    this.load.audio(AUDIO_BAT_HIT, 'sound/bat-hit-1.mp3');
    this.load.audio(AUDIO_BAT_REPEL, 'sound/bat-repel-1.mp3');
    // Manuscript audio
    this.load.audio(AUDIO_MANUSCRIPT_COLLECT, 'sound/manuscript-collect-1.mp3');
    this.load.audio(AUDIO_MANUSCRIPT_OPEN, 'sound/manuscript-open-1.mp3');
    this.load.audio(AUDIO_LIBRARY_BG, 'sound/library-1.mp3');
    // Hammer of Threia audio
    this.load.audio(AUDIO_HAMMER_COLLECT, 'sound/hammer-collect-1.mp3');
    this.load.audio(AUDIO_HAMMER_HIT, 'sound/hammer-hit-1.mp3');
    this.load.audio(AUDIO_HAMMER_BREAK, 'sound/hammer-break-1.mp3');
    this.load.audio(AUDIO_WALL_REGEN, 'sound/wall-regen-1.mp3');
    // Narrator audio
    this.load.audio(AUDIO_NARRATOR_MONK, 'sound/narrator-monk-1.mp3');
    this.load.audio(AUDIO_NARRATOR_VORATH, 'sound/narrator-vorath-1.mp3');
    // Treasure room audio
    for (let i = 1; i <= TREASURE_ROOM_MUSIC_COUNT; i++) {
      this.load.audio(`${AUDIO_TREASURE_BG}-${i}`, `sound/treasure-bg-${i}.mp3`);
    }
    this.load.audio(AUDIO_TREASURE_TICK, 'sound/treasure-tick-1.mp3');
    this.load.audio(AUDIO_TREASURE_ENTER, 'sound/treasure-enter-1.mp3');
    this.load.audio(AUDIO_TREASURE_EXIT, 'sound/treasure-exit-1.mp3');
    // Final Battle audio
    this.load.audio(AUDIO_CELESTIAL_MATERIALIZE, 'sound/celestial-materialize-1.mp3');
    this.load.audio(AUDIO_CELESTIAL_HUM, 'sound/celestial-hum-1.mp3');
    this.load.audio(AUDIO_FINAL_ENERGY, 'sound/final-energy-1.mp3');
    this.load.audio(AUDIO_THREIA_VOICE, 'sound/threia-voice-1.mp3');
    this.load.audio(AUDIO_THREIA_MUSIC, 'sound/threia-music-1.mp3');
    this.load.audio(AUDIO_VORATH_MACABRE, 'sound/vorath-macabre-1.mp3');
    this.load.audio(AUDIO_VORATH_ARENA_MUSIC, 'sound/vorath-arena-music-1.mp3');
    this.load.audio(AUDIO_VORATH_SPHERE_LAUNCH, 'sound/vorath-sphere-launch-1.mp3');
    this.load.audio(AUDIO_VORATH_SPHERE_HIT, 'sound/vorath-sphere-hit-1.mp3');
    this.load.audio(AUDIO_VORATH_SPHERE_WALL, 'sound/vorath-sphere-wall-1.mp3');
    this.load.audio(AUDIO_ALTAR_ACTIVATE, 'sound/altar-activate-1.mp3');
    this.load.audio(AUDIO_ALTAR_BEAM, 'sound/altar-beam-1.mp3');
    this.load.audio(AUDIO_VORATH_DESTROY, 'sound/vorath-destroy-1.mp3');
    this.load.audio(AUDIO_FINAL_CELESTIAL, 'sound/final-celestial-1.mp3');
    this.load.audio(AUDIO_FINAL_MUSIC, 'sound/final-music-1.mp3');
    this.load.audio(AUDIO_FINAL_TRANSITION, 'sound/final-transition-1.mp3');
    // Ambient sounds (placeholder — MP3 files not yet provided)
    this.load.audio(AUDIO_AMBIENT_WATER_DRIP, 'sound/ambient-water-drip-1.mp3');
    this.load.audio(AUDIO_AMBIENT_LAVA_CRACKLE, 'sound/ambient-lava-crackle-1.mp3');
    this.load.audio(AUDIO_AMBIENT_VORATH_WHISPER, 'sound/ambient-vorath-whisper-1.mp3');
    this.load.audio(AUDIO_AMBIENT_CREAK_1, 'sound/ambient-creak-1.mp3');
    this.load.audio(AUDIO_AMBIENT_CREAK_2, 'sound/ambient-creak-2.mp3');
    this.load.audio(AUDIO_AMBIENT_WIND, 'sound/ambient-wind-1.mp3');
    // Moving Wall audio (placeholder)
    this.load.audio(AUDIO_WALL_SHIFT_WARNING, 'sound/wall-shift-warning-1.mp3');
    this.load.audio(AUDIO_WALL_SHIFT, 'sound/wall-shift-1.mp3');
    // Secret Passage audio (placeholder)
    this.load.audio(AUDIO_SECRET_PASSAGE_OPEN, 'sound/secret-passage-open-1.mp3');
    this.load.audio(AUDIO_SECRET_PASSAGE_HINT, 'sound/secret-passage-hint-1.mp3');
    // Shadow Walker audio (placeholder)
    this.load.audio(AUDIO_SHADOW_WALKER_SPOT, 'sound/shadow-walker-spot-1.mp3');
    this.load.audio(AUDIO_SHADOW_WALKER_HIT, 'sound/shadow-walker-hit-1.mp3');
    this.load.audio(AUDIO_SHADOW_WALKER_DESPAWN, 'sound/shadow-walker-despawn-1.mp3');
    // Bat Nest audio (placeholder)
    this.load.audio(AUDIO_BAT_NEST_ACTIVATE, 'sound/bat-nest-activate-1.mp3');
    this.load.audio(AUDIO_BAT_NEST_DESTROY, 'sound/bat-nest-destroy-1.mp3');
  }

  create() {
    // Update loading overlay status while generating textures
    const statusText = document.getElementById('loading-status');
    if (statusText) statusText.textContent = 'Generating textures';

    this._generatePlayerTexture();
    this._generateWallTexture();
    this._generateWallBrickTexture();
    this._generateWallStoneTexture();
    this._generateWallWoodTexture();
    this._generateWallMetalTexture();
    this._generateWallLatticeTexture();
    this._generateWallRuinsTexture();
    this._generateWallMarbleTexture();
    this._generateWallAdobeTexture();
    this._generateFruitTexture();
    this._generateChickenTexture();
    this._generateTunaTexture();
    this._generateCoinTexture();
    this._generateLifeIcon();
    this._generateExitTexture();
    this._generateDustTexture();
    this._generateGlowTexture();
    this._generateWaterTexture();
    this._generateLavaTexture();
    this._generateSmokeTexture();
    this._generateLavaRimTexture();
    this._generatePoisonTexture();
    this._generatePoisonRimTexture();
    this._generateSpikeTexture();
    this._generateSpikeRimTexture();
    this._generateBloodTexture();
    this._generateKeyTextures();
    this._generateChestTextures();
    this._generateLootHeartTexture();
    this._generateLootStarTexture();
    this._generateLootAgmuthTexture();
    this._generateLootCoinBagTexture();
    this._generateCompassTexture();
    this._generateCompassNeedleTexture();
    this._generateThreiaTotemTexture();
    this._generateThreiaGlowTexture();
    this._generateShopTextures();
    this._generateBatTextures();
    this._generateManuscriptTexture();
    this._generateHammerOfThreiaFloorTexture();
    this._generateWallDebrisTexture();
    this._generateGoldenExitTexture();
    this._generateGoldenExitSpentTexture();
    // Final Battle textures
    this._generateCelestialExitTexture();
    this._generateVorathMassTexture();
    this._generateAltarThreiaTexture();
    this._generateDarkSphereTexture();
    this._generateBeamParticleTexture();
    // Medium-priority feature textures
    this._generateSecretWallTexture();
    this._generateShadowWalkerTexture();
    this._generateBatNestTexture();
    this._generateBatNestDestroyedTexture();
    this._generateWallCrumbledTexture();

    // Fade out loading overlay, then transition to title
    const overlay = document.getElementById('loading-overlay');
    if (overlay) {
      overlay.classList.add('fade-out');
      // Remove from DOM after the CSS transition completes
      overlay.addEventListener('transitionend', () => overlay.remove(), { once: true });
    }
    this.scene.start(SCENE_TITLE);
  }

  // ---------------------------------------------------------------
  // Texture generators
  // ---------------------------------------------------------------

  /** Player: a small filled circle with a subtle outline. */
  _generatePlayerTexture() {
    const size = PLAYER_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Outer glow ring
    g.fillStyle(COLOR_WHITE, 0.3);
    g.fillCircle(size / 2, size / 2, size / 2);

    // Inner solid circle
    g.fillStyle(COLOR_WHITE, 1);
    g.fillCircle(size / 2, size / 2, size / 2 - 2);

    g.generateTexture(TEX_PLAYER, size, size);
    g.destroy();
  }

  /** Wall tile: a filled white square. */
  _generateWallTexture() {
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    g.fillStyle(COLOR_WHITE, 1);
    g.fillRect(0, 0, TILE_SIZE, TILE_SIZE);

    g.generateTexture(TEX_WALL, TILE_SIZE, TILE_SIZE);
    g.destroy();
  }

  /**
   * Brick wall tile: two rows of offset bricks separated by thin mortar lines.
   * Preserves monochrome newspaper aesthetic with subtle mortar detail.
   */
  _generateWallBrickTexture() {
    const S = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Fill base with mortar color
    g.fillStyle(COLOR_WALL_MORTAR, 1);
    g.fillRect(0, 0, S, S);

    const mortarW = 1;          // mortar line thickness
    const brickH = (S - 3 * mortarW) / 2; // two rows of bricks + 3 mortar lines (top, mid, bottom)
    const brickW = (S - 3 * mortarW) / 2; // two bricks per row + 3 mortar lines

    // Row 1 (top): two bricks side by side
    g.fillStyle(COLOR_WALL_BRICK_FACE, 1);
    g.fillRect(mortarW, mortarW, brickW, brickH);
    g.fillRect(mortarW * 2 + brickW, mortarW, brickW, brickH);

    // Row 2 (bottom): offset by half a brick width
    const halfBrick = Math.floor(brickW / 2);
    const y2 = mortarW * 2 + brickH;
    // Left half-brick
    g.fillRect(mortarW, y2, halfBrick, brickH);
    // Center full brick
    g.fillRect(mortarW * 2 + halfBrick, y2, brickW, brickH);
    // Right half-brick
    const rightX = mortarW * 3 + halfBrick + brickW;
    const rightW = S - rightX;
    if (rightW > 0) {
      g.fillRect(rightX, y2, rightW, brickH);
    }

    g.generateTexture(TEX_WALL_BRICK, S, S);
    g.destroy();
  }

  /**
   * Stone wall tile: irregular stone blocks with subtle gray variation.
   * Rough seam lines divide the tile into ~4 unequal blocks with tonal variation.
   */
  _generateWallStoneTexture() {
    const S = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Base fill
    g.fillStyle(COLOR_WALL_STONE_LIGHT, 1);
    g.fillRect(0, 0, S, S);

    // Divide into 4 irregular blocks using offset seam lines
    const seam = 1;
    const midX = Math.floor(S * 0.45);   // vertical seam slightly off-center
    const midY = Math.floor(S * 0.55);   // horizontal seam slightly off-center

    // Draw seam lines (thin dark grooves)
    g.fillStyle(COLOR_WALL_STONE_SEAM, 1);
    g.fillRect(midX, 0, seam, S);        // vertical seam
    g.fillRect(0, midY, S, seam);        // horizontal seam

    // Tint individual blocks for tonal variation
    // Top-left: light (already base)
    // Top-right: slightly darker
    g.fillStyle(COLOR_WALL_STONE_MID, 1);
    g.fillRect(midX + seam, 0, S - midX - seam, midY);

    // Bottom-left: mid tone
    g.fillStyle(COLOR_WALL_STONE_MID, 0.6);
    g.fillRect(0, midY + seam, midX, S - midY - seam);

    // Add subtle surface texture: a few tiny speckle dots
    g.fillStyle(COLOR_WALL_STONE_SEAM, 0.3);
    g.fillRect(4, 3, 1, 1);
    g.fillRect(S - 5, 7, 1, 1);
    g.fillRect(7, S - 4, 1, 1);
    g.fillRect(S - 8, S - 6, 2, 1);
    g.fillRect(2, Math.floor(S / 2) - 3, 1, 1);

    // Thin highlight edge on top of some blocks (subtle chisel mark)
    g.lineStyle(0.5, COLOR_WHITE, 0.4);
    g.lineBetween(1, 1, midX - 1, 1);                         // top-left block top edge
    g.lineBetween(midX + seam + 1, midY + seam + 1,
                  S - 2, midY + seam + 1);                     // bottom-right block top edge

    g.generateTexture(TEX_WALL_STONE, S, S);
    g.destroy();
  }

  /**
   * Wood wall tile: horizontal planks with subtle grain lines and warm tint.
   * Uses very muted warm tones to hint at wood without breaking the grayscale feel.
   */
  _generateWallWoodTexture() {
    const S = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Base plank color (warm off-white)
    g.fillStyle(COLOR_WALL_WOOD_PLANK, 1);
    g.fillRect(0, 0, S, S);

    // Three horizontal planks separated by dark gap lines
    const gapH = 1;
    const plankH = Math.floor((S - 2 * gapH) / 3);
    const gap1Y = plankH;
    const gap2Y = plankH * 2 + gapH;

    // Plank gap lines
    g.fillStyle(COLOR_WALL_WOOD_GAP, 1);
    g.fillRect(0, gap1Y, S, gapH);
    g.fillRect(0, gap2Y, S, gapH);

    // Subtle horizontal grain lines within each plank
    g.lineStyle(0.5, COLOR_WALL_WOOD_GRAIN, 0.5);
    // Plank 1 grains
    g.lineBetween(1, 3, S - 2, 3);
    g.lineBetween(2, 5, S - 4, 5);
    // Plank 2 grains
    g.lineBetween(0, gap1Y + gapH + 2, S - 1, gap1Y + gapH + 2);
    g.lineBetween(3, gap1Y + gapH + 4, S - 2, gap1Y + gapH + 4);
    // Plank 3 grains
    g.lineBetween(1, gap2Y + gapH + 2, S - 3, gap2Y + gapH + 2);
    g.lineBetween(0, gap2Y + gapH + 5, S - 1, gap2Y + gapH + 5);

    // A small knot detail on one plank
    g.fillStyle(COLOR_WALL_WOOD_GAP, 0.5);
    g.fillCircle(Math.floor(S * 0.7), gap1Y + gapH + Math.floor(plankH / 2), 1.5);

    g.generateTexture(TEX_WALL_WOOD, S, S);
    g.destroy();
  }

  /**
   * Metal wall tile: industrial steel plates with rivets at corners.
   * Cold gray tones with subtle bevel edges and bright rivet dots.
   */
  _generateWallMetalTexture() {
    const S = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Base plate
    g.fillStyle(COLOR_WALL_METAL_PLATE, 1);
    g.fillRect(0, 0, S, S);

    // Bevel: darker edges (bottom & right) to give depth
    g.fillStyle(COLOR_WALL_METAL_DARK, 1);
    g.fillRect(0, S - 1, S, 1);       // bottom edge
    g.fillRect(S - 1, 0, 1, S);       // right edge

    // Lighter top & left edge (highlight bevel)
    g.fillStyle(COLOR_WALL_METAL_RIVET, 0.6);
    g.fillRect(0, 0, S, 1);           // top edge
    g.fillRect(0, 0, 1, S);           // left edge

    // Plate seam: horizontal line across middle
    g.fillStyle(COLOR_WALL_METAL_SEAM, 1);
    g.fillRect(0, Math.floor(S / 2), S, 1);

    // Rivets at the 4 corners (small bright dots)
    const rivetOffset = 3;
    const rivetR = 1.2;
    g.fillStyle(COLOR_WALL_METAL_RIVET, 1);
    g.fillCircle(rivetOffset, rivetOffset, rivetR);
    g.fillCircle(S - rivetOffset, rivetOffset, rivetR);
    g.fillCircle(rivetOffset, S - rivetOffset, rivetR);
    g.fillCircle(S - rivetOffset, S - rivetOffset, rivetR);

    // Subtle surface scratch
    g.lineStyle(0.5, COLOR_WALL_METAL_SEAM, 0.3);
    g.lineBetween(5, 4, S - 6, 6);

    g.generateTexture(TEX_WALL_METAL, S, S);
    g.destroy();
  }

  /**
   * Lattice wall tile: diagonal cross-hatch bars over a dark void.
   * Creates a cage / grate feel with bright bars over darkness.
   */
  _generateWallLatticeTexture() {
    const S = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Dark background (the void behind the grate)
    g.fillStyle(COLOR_WALL_LATTICE_BG, 1);
    g.fillRect(0, 0, S, S);

    // Thin border frame
    g.lineStyle(1, COLOR_WALL_LATTICE_BAR, 0.8);
    g.strokeRect(0.5, 0.5, S - 1, S - 1);

    // Diagonal bars (top-left to bottom-right)
    g.lineStyle(1.5, COLOR_WALL_LATTICE_BAR, 0.9);
    const step = 8;
    for (let offset = -S; offset <= S; offset += step) {
      g.lineBetween(offset, 0, offset + S, S);
    }
    // Diagonal bars (top-right to bottom-left)
    for (let offset = -S; offset <= S; offset += step) {
      g.lineBetween(S + offset, 0, offset, S);
    }

    // Highlight on some intersections
    g.fillStyle(COLOR_WALL_LATTICE_HIGHLIGHT, 0.5);
    g.fillCircle(S / 2, S / 2, 1);
    g.fillCircle(S / 2, 0, 0.8);
    g.fillCircle(0, S / 2, 0.8);
    g.fillCircle(S, S / 2, 0.8);
    g.fillCircle(S / 2, S, 0.8);

    g.generateTexture(TEX_WALL_LATTICE, S, S);
    g.destroy();
  }

  /**
   * Ruins wall tile: weathered stone blocks with cracks and missing chunks.
   * Gives a sense of ancient, crumbling labyrinth walls.
   */
  _generateWallRuinsTexture() {
    const S = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Base weathered surface
    g.fillStyle(COLOR_WALL_RUINS_BLOCK, 1);
    g.fillRect(0, 0, S, S);

    // Block seams (similar to stone but more irregular)
    g.fillStyle(COLOR_WALL_RUINS_CRACK, 0.6);
    g.fillRect(0, Math.floor(S * 0.4), S, 1);
    g.fillRect(Math.floor(S * 0.55), 0, 1, S);

    // Diagonal crack line across the tile
    g.lineStyle(1, COLOR_WALL_RUINS_CRACK, 0.8);
    g.lineBetween(2, 1, Math.floor(S * 0.45), Math.floor(S * 0.4));
    g.lineBetween(Math.floor(S * 0.45), Math.floor(S * 0.4),
                  Math.floor(S * 0.5), Math.floor(S * 0.65));

    // Secondary crack
    g.lineStyle(0.5, COLOR_WALL_RUINS_CRACK, 0.5);
    g.lineBetween(S - 3, S - 2, S - 8, Math.floor(S * 0.5));

    // Missing chunk / shadow (small dark rectangle, like crumbled corner)
    g.fillStyle(COLOR_WALL_RUINS_SHADOW, 0.7);
    g.fillRect(S - 5, S - 4, 4, 3);

    // A few speckle marks (weathering)
    g.fillStyle(COLOR_WALL_RUINS_CRACK, 0.25);
    g.fillRect(3, S - 6, 2, 1);
    g.fillRect(S - 10, 3, 1, 2);
    g.fillRect(8, Math.floor(S * 0.6), 1, 1);

    // Subtle lighter edge on one block (eroded highlight)
    g.lineStyle(0.5, COLOR_WHITE, 0.25);
    g.lineBetween(1, 1, Math.floor(S * 0.53), 1);

    g.generateTexture(TEX_WALL_RUINS, S, S);
    g.destroy();
  }

  /**
   * Marble wall tile: polished surface with subtle diagonal veins.
   * Elegant, clean look that contrasts with the rougher materials.
   */
  _generateWallMarbleTexture() {
    const S = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Polished base surface
    g.fillStyle(COLOR_WALL_MARBLE_BASE, 1);
    g.fillRect(0, 0, S, S);

    // Primary diagonal vein (sweeping curve)
    g.lineStyle(1.2, COLOR_WALL_MARBLE_VEIN, 0.6);
    g.lineBetween(2, S - 3, Math.floor(S * 0.4), Math.floor(S * 0.3));
    g.lineBetween(Math.floor(S * 0.4), Math.floor(S * 0.3),
                  S - 2, Math.floor(S * 0.15));

    // Secondary thinner vein running parallel-ish
    g.lineStyle(0.8, COLOR_WALL_MARBLE_VEIN2, 0.4);
    g.lineBetween(0, Math.floor(S * 0.6), Math.floor(S * 0.55), Math.floor(S * 0.1));

    // Tertiary delicate vein
    g.lineStyle(0.5, COLOR_WALL_MARBLE_VEIN, 0.3);
    g.lineBetween(Math.floor(S * 0.6), S - 1,
                  S - 1, Math.floor(S * 0.5));

    // Very subtle surface polish: thin bright edge at top
    g.lineStyle(0.5, COLOR_WHITE, 0.3);
    g.lineBetween(0, 0, S, 0);

    g.generateTexture(TEX_WALL_MARBLE, S, S);
    g.destroy();
  }

  /**
   * Adobe wall tile: dried clay / mud surface with organic cracks.
   * Warm earthy tones with irregular fissure patterns.
   */
  _generateWallAdobeTexture() {
    const S = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Base dried clay
    g.fillStyle(COLOR_WALL_ADOBE_BASE, 1);
    g.fillRect(0, 0, S, S);

    // Shadow patches (uneven drying, subtle tonal variation)
    g.fillStyle(COLOR_WALL_ADOBE_SHADOW, 0.5);
    g.fillRect(1, 1, Math.floor(S * 0.45), Math.floor(S * 0.4));
    g.fillRect(Math.floor(S * 0.55), Math.floor(S * 0.55), Math.floor(S * 0.4), Math.floor(S * 0.4));

    // Organic crack network (irregular, branching lines)
    g.lineStyle(1, COLOR_WALL_ADOBE_CRACK, 0.7);
    // Main crack from top-left area
    g.lineBetween(3, 2, Math.floor(S * 0.35), Math.floor(S * 0.45));
    g.lineBetween(Math.floor(S * 0.35), Math.floor(S * 0.45),
                  Math.floor(S * 0.6), Math.floor(S * 0.4));
    // Branch
    g.lineStyle(0.7, COLOR_WALL_ADOBE_CRACK, 0.5);
    g.lineBetween(Math.floor(S * 0.35), Math.floor(S * 0.45),
                  Math.floor(S * 0.3), Math.floor(S * 0.75));

    // Second crack cluster
    g.lineStyle(0.8, COLOR_WALL_ADOBE_CRACK, 0.6);
    g.lineBetween(S - 4, S - 3, Math.floor(S * 0.65), Math.floor(S * 0.55));

    // Small granular speckles (texture)
    g.fillStyle(COLOR_WALL_ADOBE_CRACK, 0.2);
    g.fillRect(S - 7, 4, 1, 1);
    g.fillRect(5, S - 5, 1, 1);
    g.fillRect(Math.floor(S * 0.7), Math.floor(S * 0.2), 2, 1);
    g.fillRect(2, Math.floor(S * 0.65), 1, 1);

    g.generateTexture(TEX_WALL_ADOBE, S, S);
    g.destroy();
  }

  /** Fruit: a small circle (like an apple/berry). */
  _generateFruitTexture() {
    const size = 14;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Body
    g.fillStyle(COLOR_WHITE, 1);
    g.fillCircle(size / 2, size / 2 + 1, size / 2 - 2);

    // Stem
    g.lineStyle(1.5, COLOR_WHITE, 1);
    g.lineBetween(size / 2, 1, size / 2 + 1, size / 2 - 3);

    // Small leaf
    g.fillStyle(COLOR_WHITE, 0.8);
    g.fillEllipse(size / 2 + 3, 3, 4, 2);

    g.generateTexture(TEX_FOOD_FRUIT, size, size);
    g.destroy();
  }

  /** Chicken leg: a drumstick shape. */
  _generateChickenTexture() {
    const size = 14;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Meat part (rounded top)
    g.fillStyle(COLOR_WHITE, 1);
    g.fillCircle(size / 2, 4, 4);

    // Bone shaft
    g.lineStyle(2, COLOR_WHITE, 1);
    g.lineBetween(size / 2, 6, size / 2, size - 2);

    // Bone end knobs
    g.fillStyle(COLOR_WHITE, 1);
    g.fillCircle(size / 2 - 1.5, size - 2, 1.5);
    g.fillCircle(size / 2 + 1.5, size - 2, 1.5);

    g.generateTexture(TEX_FOOD_CHICKEN, size, size);
    g.destroy();
  }

  /** Tuna can: a small rectangle with lines. */
  _generateTunaTexture() {
    const w = 14;
    const h = 12;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Can body
    g.lineStyle(1.5, COLOR_WHITE, 1);
    g.strokeRoundedRect(1, 2, w - 2, h - 4, 2);

    // Label lines
    g.lineStyle(1, COLOR_WHITE, 0.6);
    g.lineBetween(3, 5, w - 3, 5);
    g.lineBetween(3, 7, w - 3, 7);

    // Can lid top
    g.fillStyle(COLOR_WHITE, 0.4);
    g.fillRect(2, 1, w - 4, 2);

    g.generateTexture(TEX_FOOD_TUNA, w, h);
    g.destroy();
  }

  /** Coin: a small circle with a "$" or "C" symbol and a glow ring. */
  _generateCoinTexture() {
    const size = 14;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;
    const cy = size / 2;

    // Outer glow ring (subtle)
    g.fillStyle(COLOR_WHITE, 0.15);
    g.fillCircle(cx, cy, size / 2);

    // Coin body outline
    g.lineStyle(1.5, COLOR_WHITE, 1);
    g.strokeCircle(cx, cy, size / 2 - 2);

    // Inner detail: small "C" mark
    g.lineStyle(1, COLOR_WHITE, 0.8);
    g.beginPath();
    g.arc(cx, cy, 3, -0.8, 0.8, true);
    g.strokePath();

    g.generateTexture(TEX_COIN, size, size);
    g.destroy();
  }

  /** Life icon: a small heart shape. */
  _generateLifeIcon() {
    const size = 20;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    g.fillStyle(COLOR_WHITE, 1);

    // Heart shape: two overlapping circles for the lobes + a triangle for the point.
    // Larger canvas (20px) for a cleaner silhouette; displayed scaled down in the HUD.
    const cx = size / 2;          // 10
    const r = 4;                  // lobe radius

    // Left and right lobes — offset horizontally from center
    g.fillCircle(cx - r + 1, r + 2, r);   // left lobe  (7, 6)
    g.fillCircle(cx + r - 1, r + 2, r);   // right lobe (13, 6)

    // Bottom triangle connecting the lobes to the tip
    g.fillTriangle(
      cx - r * 2 + 1, r + 3,     // top-left
      cx + r * 2 - 1, r + 3,     // top-right
      cx, size - 1,               // bottom tip
    );

    g.generateTexture(TEX_LIFE_ICON, size, size);
    g.destroy();
  }

  /** Exit marker: a pulsing target/door indicator. */
  _generateExitTexture() {
    const size = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Outer ring
    g.lineStyle(2, COLOR_WHITE, 1);
    g.strokeCircle(size / 2, size / 2, size / 2 - 3);

    // Inner ring
    g.lineStyle(1, COLOR_WHITE, 0.7);
    g.strokeCircle(size / 2, size / 2, size / 4);

    // Center dot
    g.fillStyle(COLOR_WHITE, 1);
    g.fillCircle(size / 2, size / 2, 2);

    g.generateTexture(TEX_EXIT, size, size);
    g.destroy();
  }

  /** Golden exit marker: a golden glowing target for Vorath's Treasure Room. */
  _generateGoldenExitTexture() {
    const size = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Outer golden glow aura
    g.fillStyle(COLOR_GOLDEN_EXIT, 0.12);
    g.fillCircle(size / 2, size / 2, size / 2);
    g.fillStyle(COLOR_GOLDEN_EXIT, 0.2);
    g.fillCircle(size / 2, size / 2, size / 2 - 2);

    // Outer ring — golden
    g.lineStyle(2, COLOR_GOLDEN_EXIT, 1);
    g.strokeCircle(size / 2, size / 2, size / 2 - 3);

    // Inner ring — darker gold
    g.lineStyle(1, COLOR_GOLDEN_EXIT_DARK, 0.9);
    g.strokeCircle(size / 2, size / 2, size / 4);

    // Center dot — bright gold
    g.fillStyle(COLOR_GOLDEN_EXIT, 1);
    g.fillCircle(size / 2, size / 2, 3);

    g.generateTexture(TEX_GOLDEN_EXIT, size, size);
    g.destroy();
  }

  /** Spent golden exit: deteriorated, drained of magical energy. */
  _generateGoldenExitSpentTexture() {
    const size = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Faded, broken outer ring — dull dark gold with gaps
    g.lineStyle(1, COLOR_GOLDEN_EXIT_DARK, 0.3);
    g.strokeCircle(size / 2, size / 2, size / 2 - 3);

    // Cracked inner ring — very faint
    g.lineStyle(1, COLOR_GOLDEN_EXIT_DARK, 0.15);
    g.strokeCircle(size / 2, size / 2, size / 4);

    // Dim center — barely visible
    g.fillStyle(COLOR_GOLDEN_EXIT_DARK, 0.2);
    g.fillCircle(size / 2, size / 2, 2);

    // Small crack lines radiating from center
    g.lineStyle(1, COLOR_GOLDEN_EXIT_DARK, 0.15);
    for (let i = 0; i < 4; i++) {
      const angle = (Math.PI / 2) * i + Math.PI / 6;
      g.beginPath();
      g.moveTo(size / 2 + Math.cos(angle) * 3, size / 2 + Math.sin(angle) * 3);
      g.lineTo(size / 2 + Math.cos(angle) * (size / 2 - 4), size / 2 + Math.sin(angle) * (size / 2 - 4));
      g.strokePath();
    }

    g.generateTexture(TEX_GOLDEN_EXIT_SPENT, size, size);
    g.destroy();
  }

  /** Dust particle: a tiny soft white circle for the player trail effect. */
  _generateDustTexture() {
    const size = 6;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Soft circle with gradient-like falloff
    g.fillStyle(COLOR_WHITE, 0.8);
    g.fillCircle(size / 2, size / 2, size / 2);
    g.fillStyle(COLOR_WHITE, 0.4);
    g.fillCircle(size / 2, size / 2, size / 2 - 1);

    g.generateTexture(TEX_DUST, size, size);
    g.destroy();
  }

  /** Glow particle: a soft radial glow for the level-complete flare effect. */
  _generateGlowTexture() {
    const size = 16;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;
    const cy = size / 2;

    // Multiple concentric circles for a soft glow feel
    g.fillStyle(COLOR_WHITE, 0.15);
    g.fillCircle(cx, cy, size / 2);
    g.fillStyle(COLOR_WHITE, 0.3);
    g.fillCircle(cx, cy, size / 3);
    g.fillStyle(COLOR_WHITE, 0.6);
    g.fillCircle(cx, cy, size / 5);
    g.fillStyle(COLOR_WHITE, 1);
    g.fillCircle(cx, cy, 1.5);

    g.generateTexture(TEX_GLOW, size, size);
    g.destroy();
  }

  /**
   * Water puddle tile: a bluish-tinted tile with pixelated water dots.
   * Uses a dark blue base with scattered lighter blue pixels to simulate
   * a retro water effect while staying within the minimalist aesthetic.
   */
  _generateWaterTexture() {
    const size = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Dark blue base
    g.fillStyle(0x1a2a3a, 0.85);
    g.fillRect(0, 0, size, size);

    // Pixelated water pattern — scattered blue-ish squares of varying shades
    const pixelSize = 3;
    const cols = Math.floor(size / pixelSize);
    const rows = Math.floor(size / pixelSize);

    // Deterministic pseudo-random using a simple LCG for consistency
    let rng = 42;
    const next = () => {
      rng = (rng * 1664525 + 1013904223) & 0x7fffffff;
      return rng / 0x7fffffff;
    };

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const roll = next();
        if (roll < 0.35) {
          // Medium blue pixel
          const alpha = 0.4 + next() * 0.3;
          g.fillStyle(0x3366aa, alpha);
          g.fillRect(c * pixelSize, r * pixelSize, pixelSize, pixelSize);
        } else if (roll < 0.5) {
          // Lighter blue highlight pixel
          const alpha = 0.3 + next() * 0.25;
          g.fillStyle(0x5588cc, alpha);
          g.fillRect(c * pixelSize, r * pixelSize, pixelSize, pixelSize);
        }
      }
    }

    // Subtle edge highlight (top-left shimmer)
    g.fillStyle(0x88bbee, 0.15);
    g.fillRect(0, 0, size, 1);
    g.fillRect(0, 0, 1, size);

    g.generateTexture(TEX_WATER, size, size);
    g.destroy();
  }

  /**
   * Lava floor tile: a red/orange-tinted tile with pixelated lava dots.
   * Uses a dark red base with scattered orange/red pixels to simulate
   * a retro lava effect, matching the minimalist aesthetic.
   */
  _generateLavaTexture() {
    const size = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Dark red base
    g.fillStyle(0x3a1a0a, 0.9);
    g.fillRect(0, 0, size, size);

    // Pixelated lava pattern — scattered red/orange squares of varying shades
    const pixelSize = 3;
    const cols = Math.floor(size / pixelSize);
    const rows = Math.floor(size / pixelSize);

    // Deterministic pseudo-random using a simple LCG for consistency
    let rng = 77;
    const next = () => {
      rng = (rng * 1664525 + 1013904223) & 0x7fffffff;
      return rng / 0x7fffffff;
    };

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const roll = next();
        if (roll < 0.3) {
          // Deep red pixel
          const alpha = 0.5 + next() * 0.3;
          g.fillStyle(0xaa3300, alpha);
          g.fillRect(c * pixelSize, r * pixelSize, pixelSize, pixelSize);
        } else if (roll < 0.45) {
          // Bright orange highlight pixel
          const alpha = 0.4 + next() * 0.3;
          g.fillStyle(0xcc5500, alpha);
          g.fillRect(c * pixelSize, r * pixelSize, pixelSize, pixelSize);
        } else if (roll < 0.55) {
          // Hot yellow-orange accent pixel
          const alpha = 0.25 + next() * 0.2;
          g.fillStyle(0xff7700, alpha);
          g.fillRect(c * pixelSize, r * pixelSize, pixelSize, pixelSize);
        }
      }
    }

    // Subtle edge highlight (top-left shimmer — like glowing embers)
    g.fillStyle(0xff5522, 0.12);
    g.fillRect(0, 0, size, 1);
    g.fillRect(0, 0, 1, size);

    g.generateTexture(TEX_LAVA, size, size);
    g.destroy();
  }

  /**
   * Smoke particle: a small, soft gray circle for the lava smoke trail.
   */
  _generateSmokeTexture() {
    const size = 8;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Soft gray concentric circles for a smoky feel
    g.fillStyle(0x999999, 0.3);
    g.fillCircle(size / 2, size / 2, size / 2);
    g.fillStyle(0xbbbbbb, 0.2);
    g.fillCircle(size / 2, size / 2, size / 3);
    g.fillStyle(0xdddddd, 0.15);
    g.fillCircle(size / 2, size / 2, size / 5);

    g.generateTexture(TEX_SMOKE, size, size);
    g.destroy();
  }

  /**
   * Lava rim texture: a hollow red/orange ring (donut shape) used as a
   * "burning" indicator around the player sprite while walking on lava.
   * Slightly larger than the player to form a visible halo without
   * obscuring the player itself. Uses a Canvas texture so the centre
   * is truly transparent (Phaser Graphics cannot erase pixels).
   */
  _generateLavaRimTexture() {
    const size = PLAYER_SIZE + 10;
    const canvas = this.textures.createCanvas(TEX_LAVA_RIM, size, size);
    const ctx = canvas.context;
    const cx = size / 2;
    const cy = size / 2;

    // Outer soft glow ring (wide, faint)
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2 - 1, 0, Math.PI * 2);
    ctx.arc(cx, cy, size / 2 - 4, 0, Math.PI * 2, true); // counter-clockwise = cut-out
    ctx.fillStyle = 'rgba(255, 34, 0, 0.25)';
    ctx.fill();

    // Main bright ring
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2 - 2, 0, Math.PI * 2);
    ctx.arc(cx, cy, size / 2 - 5, 0, Math.PI * 2, true);
    ctx.fillStyle = 'rgba(255, 68, 0, 0.8)';
    ctx.fill();

    // Inner hot-white edge (very thin, bright)
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2 - 4, 0, Math.PI * 2);
    ctx.arc(cx, cy, size / 2 - 5.5, 0, Math.PI * 2, true);
    ctx.fillStyle = 'rgba(255, 160, 60, 0.6)';
    ctx.fill();

    canvas.refresh();
  }

  /**
   * Poison rim texture: a greenish glowing ring around the player
   * while poisoned. Mirrors the lava rim but with green/toxic tones.
   * Uses Canvas API for a truly transparent center.
   */
  _generatePoisonRimTexture() {
    const size = PLAYER_SIZE + 10;
    const canvas = this.textures.createCanvas(TEX_POISON_RIM, size, size);
    const ctx = canvas.context;
    const cx = size / 2;
    const cy = size / 2;

    // Outer soft glow ring (wide, faint)
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2 - 1, 0, Math.PI * 2);
    ctx.arc(cx, cy, size / 2 - 4, 0, Math.PI * 2, true);
    ctx.fillStyle = 'rgba(0, 200, 0, 0.25)';
    ctx.fill();

    // Main bright ring
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2 - 2, 0, Math.PI * 2);
    ctx.arc(cx, cy, size / 2 - 5, 0, Math.PI * 2, true);
    ctx.fillStyle = 'rgba(0, 180, 30, 0.8)';
    ctx.fill();

    // Inner toxic-bright edge (very thin, bright)
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2 - 4, 0, Math.PI * 2);
    ctx.arc(cx, cy, size / 2 - 5.5, 0, Math.PI * 2, true);
    ctx.fillStyle = 'rgba(100, 255, 80, 0.6)';
    ctx.fill();

    canvas.refresh();
  }

  /**
   * Poison floor tile: a greenish-tinted tile with pixelated toxic dots.
   * Uses a dark green base with scattered lighter green pixels to simulate
   * a retro poison/toxic effect, matching the minimalist aesthetic.
   */
  _generatePoisonTexture() {
    const size = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Dark green base
    g.fillStyle(0x1a3a1a, 0.85);
    g.fillRect(0, 0, size, size);

    // Pixelated poison pattern — scattered green squares of varying shades
    const pixelSize = 3;
    const cols = Math.floor(size / pixelSize);
    const rows = Math.floor(size / pixelSize);

    // Deterministic pseudo-random using a simple LCG for consistency
    let rng = 99;
    const next = () => {
      rng = (rng * 1664525 + 1013904223) & 0x7fffffff;
      return rng / 0x7fffffff;
    };

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const roll = next();
        if (roll < 0.3) {
          // Medium green pixel
          const alpha = 0.4 + next() * 0.3;
          g.fillStyle(0x33aa33, alpha);
          g.fillRect(c * pixelSize, r * pixelSize, pixelSize, pixelSize);
        } else if (roll < 0.45) {
          // Lighter green highlight pixel
          const alpha = 0.3 + next() * 0.25;
          g.fillStyle(0x55cc55, alpha);
          g.fillRect(c * pixelSize, r * pixelSize, pixelSize, pixelSize);
        } else if (roll < 0.52) {
          // Bright green accent pixel
          const alpha = 0.2 + next() * 0.2;
          g.fillStyle(0x88ee88, alpha);
          g.fillRect(c * pixelSize, r * pixelSize, pixelSize, pixelSize);
        }
      }
    }

    // Subtle edge highlight (top-left shimmer — like toxic glow)
    g.fillStyle(0x88ee88, 0.12);
    g.fillRect(0, 0, size, 1);
    g.fillRect(0, 0, 1, size);

    g.generateTexture(TEX_POISON, size, size);
    g.destroy();
  }

  /**
   * Spike floor tile: a dark metallic gray base with triangular spike
   * shapes drawn in lighter gray to convey sharp, dangerous ground.
   * Uses a pixelated pattern of small pointed triangles in rows.
   */
  _generateSpikeTexture() {
    const size = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Dark metallic base
    g.fillStyle(0x2a2a2a, 0.9);
    g.fillRect(0, 0, size, size);

    // Grid of small spike triangles (3x3 pattern across the tile)
    const spikeW = 6;       // Width of each spike base
    const spikeH = 8;       // Height of each spike point
    const cols = 3;
    const rows = 2;
    const padX = (size - cols * spikeW) / (cols + 1);
    const padY = (size - rows * spikeH) / (rows + 1);

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const bx = padX + c * (spikeW + padX);
        const by = padY + r * (spikeH + padY) + spikeH;

        // Spike body (upward-pointing triangle)
        g.fillStyle(0x888888, 0.9);
        g.fillTriangle(
          bx, by,                           // bottom-left
          bx + spikeW, by,                  // bottom-right
          bx + spikeW / 2, by - spikeH,    // top (point)
        );

        // Bright tip highlight
        g.fillStyle(0xcccccc, 0.7);
        g.fillTriangle(
          bx + spikeW / 2 - 1, by - spikeH + 2,
          bx + spikeW / 2 + 1, by - spikeH + 2,
          bx + spikeW / 2, by - spikeH,
        );
      }
    }

    // Subtle metallic edge highlights
    g.fillStyle(0x555555, 0.2);
    g.fillRect(0, 0, size, 1);
    g.fillRect(0, 0, 1, size);

    g.generateTexture(TEX_SPIKE, size, size);
    g.destroy();
  }

  /**
   * Spike rim texture: a blood-red glowing ring around the player
   * while wounded by spikes. Mirrors the lava/poison rim pattern
   * but with dark red/crimson tones. Uses Canvas API for a truly
   * transparent center.
   */
  _generateSpikeRimTexture() {
    const size = PLAYER_SIZE + 10;
    const canvas = this.textures.createCanvas(TEX_SPIKE_RIM, size, size);
    const ctx = canvas.context;
    const cx = size / 2;
    const cy = size / 2;

    // Outer soft glow ring (wide, faint)
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2 - 1, 0, Math.PI * 2);
    ctx.arc(cx, cy, size / 2 - 4, 0, Math.PI * 2, true);
    ctx.fillStyle = 'rgba(180, 0, 0, 0.25)';
    ctx.fill();

    // Main bright ring
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2 - 2, 0, Math.PI * 2);
    ctx.arc(cx, cy, size / 2 - 5, 0, Math.PI * 2, true);
    ctx.fillStyle = 'rgba(200, 20, 20, 0.8)';
    ctx.fill();

    // Inner blood-bright edge (very thin, bright)
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2 - 4, 0, Math.PI * 2);
    ctx.arc(cx, cy, size / 2 - 5.5, 0, Math.PI * 2, true);
    ctx.fillStyle = 'rgba(255, 80, 80, 0.6)';
    ctx.fill();

    canvas.refresh();
  }

  /**
   * Blood droplet texture: a small dark red circle used as a particle
   * for the blood trail effect when the player is wounded by spikes.
   */
  _generateBloodTexture() {
    const size = 6;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Soft dark red circle with falloff
    g.fillStyle(0xcc2222, 0.8);
    g.fillCircle(size / 2, size / 2, size / 2);
    g.fillStyle(0x991111, 0.5);
    g.fillCircle(size / 2, size / 2, size / 2 - 1);

    g.generateTexture(TEX_BLOOD, size, size);
    g.destroy();
  }

  // ---------------------------------------------------------------
  // Key & Chest textures
  // ---------------------------------------------------------------

  /**
   * Generate key textures for all three tiers (metallic, copper, gold).
   * Keys are drawn as classic key silhouettes at 14x14 pixels.
   * Each tier uses its own color palette.
   */
  _generateKeyTextures() {
    const tiers = [
      { tex: TEX_KEY_METALLIC, color: COLOR_METALLIC, dark: COLOR_METALLIC_DARK, highlight: 0xcccccc },
      { tex: TEX_KEY_COPPER, color: COLOR_COPPER, dark: COLOR_COPPER_DARK, highlight: 0xd4956a },
      { tex: TEX_KEY_GOLD, color: COLOR_GOLD, dark: COLOR_GOLD_DARK, highlight: 0xffec80 },
    ];

    for (const tier of tiers) {
      const size = 14;
      const g = this.make.graphics({ x: 0, y: 0, add: false });

      // Key head — a ring (circle with hole)
      const headCx = 5;
      const headCy = 4;
      const headR = 3.5;
      g.fillStyle(tier.color, 1);
      g.fillCircle(headCx, headCy, headR);
      g.fillStyle(0x000000, 1);
      g.fillCircle(headCx, headCy, 1.5);

      // Highlight on the ring
      g.fillStyle(tier.highlight, 0.5);
      g.fillCircle(headCx - 1, headCy - 1, 1);

      // Key shaft
      g.fillStyle(tier.color, 1);
      g.fillRect(headCx, headCy + 1, 2, 7);

      // Dark edge on shaft
      g.fillStyle(tier.dark, 0.6);
      g.fillRect(headCx + 1.5, headCy + 1, 0.5, 7);

      // Key teeth (two small notches on the right side of the shaft)
      g.fillStyle(tier.color, 1);
      g.fillRect(headCx + 2, headCy + 5, 2, 1.5);
      g.fillRect(headCx + 2, headCy + 7, 1.5, 1.5);

      g.generateTexture(tier.tex, size, size);
      g.destroy();
    }
  }

  /**
   * Generate chest textures for all three tiers (metallic, copper, gold).
   * Chests are drawn as small treasure chest shapes at TILE_SIZE x TILE_SIZE.
   * Each tier uses its own color palette with distinct visual style.
   */
  _generateChestTextures() {
    const tiers = [
      { tex: TEX_CHEST_METALLIC, color: COLOR_METALLIC, dark: COLOR_METALLIC_DARK, highlight: 0xcccccc, band: 0x999999 },
      { tex: TEX_CHEST_COPPER, color: COLOR_COPPER, dark: COLOR_COPPER_DARK, highlight: 0xd4956a, band: 0x9a5d2f },
      { tex: TEX_CHEST_GOLD, color: COLOR_GOLD, dark: COLOR_GOLD_DARK, highlight: 0xffec80, band: 0xcc9900 },
    ];

    for (const tier of tiers) {
      const s = TILE_SIZE;
      const g = this.make.graphics({ x: 0, y: 0, add: false });

      // Chest body
      const bx = 3;
      const by = 7;
      const bw = s - 6;
      const bh = s - 10;

      // Main body (lower rect)
      g.fillStyle(tier.dark, 1);
      g.fillRect(bx, by + bh * 0.4, bw, bh * 0.6);

      // Lid (upper rounded portion — trapezoid approximation)
      g.fillStyle(tier.color, 1);
      g.fillRect(bx, by, bw, bh * 0.45);

      // Lid arc highlight
      g.fillStyle(tier.highlight, 0.4);
      g.fillRect(bx + 2, by + 1, bw - 4, 2);

      // Horizontal band across the middle
      g.fillStyle(tier.band, 0.9);
      g.fillRect(bx, by + bh * 0.38, bw, 2);

      // Lock/clasp in the center
      g.fillStyle(tier.highlight, 0.9);
      g.fillRect(bx + bw / 2 - 2, by + bh * 0.35, 4, 4);
      g.fillStyle(tier.dark, 1);
      g.fillRect(bx + bw / 2 - 0.5, by + bh * 0.38, 1, 2);

      // Bottom edge shadow
      g.fillStyle(0x000000, 0.3);
      g.fillRect(bx, by + bh - 1, bw, 1);

      g.generateTexture(tier.tex, s, s);
      g.destroy();
    }
  }

  /**
   * Loot heart texture: a pulsating heart icon (red) used as chest loot.
   * Slightly larger than the life icon for visibility.
   */
  _generateLootHeartTexture() {
    const size = 16;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;

    // Heart shape: two circles + triangle, colored red
    const lobeR = 3.5;
    g.fillStyle(0xcc2222, 1);
    g.fillCircle(cx - 2.5, 5, lobeR);
    g.fillCircle(cx + 2.5, 5, lobeR);
    g.fillTriangle(cx - 5.5, 6, cx + 5.5, 6, cx, 14);

    // Bright highlight
    g.fillStyle(0xff4444, 0.5);
    g.fillCircle(cx - 2.5, 4, 1.5);

    g.generateTexture(TEX_LOOT_HEART, size, size);
    g.destroy();
  }

  /**
   * Loot star texture: a bright 5-pointed star used as chest loot.
   * White with a subtle glow effect.
   */
  _generateLootStarTexture() {
    const size = 16;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;
    const cy = size / 2;

    // Draw a 5-pointed star
    const outerR = 6;
    const innerR = 2.5;
    const points = 5;

    // Outer glow
    g.fillStyle(COLOR_WHITE, 0.2);
    g.fillCircle(cx, cy, outerR + 1);

    // Star polygon
    g.fillStyle(COLOR_WHITE, 1);
    g.beginPath();
    for (let i = 0; i < points * 2; i++) {
      const r = i % 2 === 0 ? outerR : innerR;
      const angle = (Math.PI / 2 * 3) + (i * Math.PI / points);
      const px = cx + Math.cos(angle) * r;
      const py = cy + Math.sin(angle) * r;
      if (i === 0) {
        g.moveTo(px, py);
      } else {
        g.lineTo(px, py);
      }
    }
    g.closePath();
    g.fillPath();

    // Center bright spot
    g.fillStyle(COLOR_WHITE, 0.8);
    g.fillCircle(cx, cy, 1.5);

    g.generateTexture(TEX_LOOT_STAR, size, size);
    g.destroy();
  }

  /**
   * Loot Agmuth texture: an inverted triangle (pointing down) with a
   * mystical appearance. Uses white with subtle inner glow.
   */
  _generateLootAgmuthTexture() {
    const size = 16;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;

    // Outer glow
    g.fillStyle(COLOR_WHITE, 0.15);
    g.fillCircle(cx, cx, 7);

    // Main inverted triangle
    g.fillStyle(COLOR_WHITE, 1);
    g.fillTriangle(
      2, 3,         // top-left
      size - 2, 3,  // top-right
      cx, size - 2, // bottom center (point)
    );

    // Inner darker triangle for depth
    g.fillStyle(0x888888, 0.4);
    g.fillTriangle(
      4, 5,
      size - 4, 5,
      cx, size - 4,
    );

    // Center mystical dot
    g.fillStyle(COLOR_WHITE, 0.9);
    g.fillCircle(cx, 7, 1.5);

    g.generateTexture(TEX_LOOT_AGMUTH, size, size);
    g.destroy();
  }

  /**
   * Loot Bag of Coins texture: a small tied pouch / sack with a "$"
   * symbol. Uses white body with a golden-ish stroke for the tie.
   */
  _generateLootCoinBagTexture() {
    const size = 18;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;

    // Outer glow ring (subtle)
    g.fillStyle(0xffd700, 0.12);
    g.fillCircle(cx, cx, size / 2);

    // Bag body — rounded rectangle / ellipse shape
    g.fillStyle(COLOR_WHITE, 0.9);
    g.fillEllipse(cx, 11, 12, 10);

    // Bag body outline
    g.lineStyle(1.2, COLOR_WHITE, 1);
    g.strokeEllipse(cx, 11, 12, 10);

    // Bag neck (narrower top part)
    g.fillStyle(COLOR_WHITE, 0.85);
    g.fillRect(cx - 3, 4, 6, 4);

    // Tie / cinch at the top of the bag
    g.lineStyle(1.5, 0xffd700, 0.9);
    g.beginPath();
    g.moveTo(cx - 3.5, 6);
    g.lineTo(cx + 3.5, 6);
    g.strokePath();

    // Tie knot — small circle at center of tie
    g.fillStyle(0xffd700, 0.8);
    g.fillCircle(cx, 5.5, 1.2);

    // Bag opening — gathered top above tie
    g.fillStyle(COLOR_WHITE, 0.7);
    g.fillTriangle(cx - 4, 5, cx + 4, 5, cx, 2);

    // "$" symbol on the bag body
    g.lineStyle(1, 0xffd700, 0.7);
    // S-curve
    g.beginPath();
    g.arc(cx - 0.5, 10, 2, -1.0, 0.6, false);
    g.strokePath();
    g.beginPath();
    g.arc(cx + 0.5, 12, 2, -1.0 + Math.PI, 0.6 + Math.PI, false);
    g.strokePath();
    // Vertical line through the S
    g.beginPath();
    g.moveTo(cx, 8);
    g.lineTo(cx, 14.5);
    g.strokePath();

    g.generateTexture(TEX_LOOT_COIN_BAG, size, size);
    g.destroy();
  }

  // ---------------------------------------------------------------
  // Compass textures
  // ---------------------------------------------------------------

  /**
   * Generate the compass pickup texture — a recognisable compass rose.
   * Circular body with cross-hairs and a diamond-shaped needle indicator.
   * Size is 18×18 so it's clearly visible on the maze floor.
   */
  _generateCompassTexture() {
    const size = 18;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;
    const cy = size / 2;

    // Outer glow halo
    g.fillStyle(COLOR_WHITE, 0.10);
    g.fillCircle(cx, cy, size / 2);

    // Compass ring
    g.lineStyle(1.5, COLOR_WHITE, 0.9);
    g.strokeCircle(cx, cy, 7);

    // Inner ring
    g.lineStyle(0.8, COLOR_WHITE, 0.4);
    g.strokeCircle(cx, cy, 4.5);

    // Cross-hair lines (N-S and E-W)
    g.lineStyle(0.7, COLOR_WHITE, 0.5);
    g.beginPath();
    g.moveTo(cx, cy - 7);
    g.lineTo(cx, cy + 7);
    g.strokePath();
    g.beginPath();
    g.moveTo(cx - 7, cy);
    g.lineTo(cx + 7, cy);
    g.strokePath();

    // North needle (white filled triangle pointing up)
    g.fillStyle(COLOR_WHITE, 1);
    g.fillTriangle(cx, cy - 6, cx - 2, cy, cx + 2, cy);

    // South needle (dimmer)
    g.fillStyle(COLOR_WHITE, 0.35);
    g.fillTriangle(cx, cy + 6, cx - 2, cy, cx + 2, cy);

    // Center pivot dot
    g.fillStyle(COLOR_WHITE, 0.9);
    g.fillCircle(cx, cy, 1.2);

    g.generateTexture(TEX_COMPASS, size, size);
    g.destroy();
  }

  /**
   * Generate the compass needle texture used in the HUD display.
   * A slim diamond/arrow shape drawn centered on a 32×32 canvas so
   * it can be rotated around its center.
   */
  _generateCompassNeedleTexture() {
    const size = 32;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;
    const cy = size / 2;

    // North half (bright white — the pointing end)
    g.fillStyle(COLOR_WHITE, 1);
    g.fillTriangle(cx, cy - 12, cx - 3, cy, cx + 3, cy);

    // South half (dim tail)
    g.fillStyle(COLOR_WHITE, 0.3);
    g.fillTriangle(cx, cy + 10, cx - 2.5, cy, cx + 2.5, cy);

    // Center dot
    g.fillStyle(COLOR_WHITE, 0.8);
    g.fillCircle(cx, cy, 1.5);

    g.generateTexture(TEX_COMPASS_NEEDLE, size, size);
    g.destroy();
  }

  // ---------------------------------------------------------------
  // Threia Totem textures
  // ---------------------------------------------------------------

  /**
   * Generate the Threia Totem floor pickup texture.
   * A small mystical pillar/obelisk shape (18×18) with an eye-like
   * symbol at the center — a divine totem left by the Goddess Threia.
   */
  _generateThreiaTotemTexture() {
    const size = 18;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;
    const cy = size / 2;

    // Outer divine halo
    g.fillStyle(COLOR_WHITE, 0.08);
    g.fillCircle(cx, cy, size / 2);

    // Totem base — a tapered trapezoid/obelisk shape
    g.fillStyle(COLOR_WHITE, 0.7);
    // Base bottom
    g.fillRect(cx - 4, cy + 3, 8, 3);
    // Main pillar body (narrower)
    g.fillStyle(COLOR_WHITE, 0.9);
    g.fillRect(cx - 3, cy - 5, 6, 10);

    // Pillar top cap — small triangle
    g.fillStyle(COLOR_WHITE, 1);
    g.fillTriangle(cx, cy - 8, cx - 3.5, cy - 5, cx + 3.5, cy - 5);

    // All-seeing eye at center — an almond/diamond eye shape
    // Eye outline (almond shape via two arcs approximated with triangles)
    g.fillStyle(0x000000, 1);
    g.fillCircle(cx, cy - 1, 2.5);
    // Eye white
    g.fillStyle(COLOR_WHITE, 1);
    g.fillCircle(cx, cy - 1, 1.8);
    // Eye pupil
    g.fillStyle(0x000000, 1);
    g.fillCircle(cx, cy - 1, 0.9);
    // Eye glint
    g.fillStyle(COLOR_WHITE, 1);
    g.fillCircle(cx + 0.5, cy - 1.5, 0.4);

    // Small decorative lines flanking the pillar
    g.lineStyle(0.6, COLOR_WHITE, 0.4);
    g.beginPath();
    g.moveTo(cx - 5, cy + 2);
    g.lineTo(cx - 5, cy - 3);
    g.strokePath();
    g.beginPath();
    g.moveTo(cx + 5, cy + 2);
    g.lineTo(cx + 5, cy - 3);
    g.strokePath();

    g.generateTexture(TEX_THREIA_TOTEM, size, size);
    g.destroy();
  }

  /**
   * Generate a soft radial glow texture for Threia prayer effects.
   * 32×32 circle with fading alpha from center to edges.
   */
  _generateThreiaGlowTexture() {
    const size = 32;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;
    const cy = size / 2;

    // Multiple concentric circles with decreasing alpha for soft glow
    const steps = 8;
    for (let i = steps; i >= 0; i--) {
      const r = (size / 2) * (i / steps);
      const a = 1 - (i / steps);
      g.fillStyle(COLOR_WHITE, a * 0.5);
      g.fillCircle(cx, cy, r);
    }

    g.generateTexture(TEX_THREIA_GLOW, size, size);
    g.destroy();
  }

  // ---------------------------------------------------------------
  // Shop item textures
  // ---------------------------------------------------------------

  /**
   * Generate all 12 shop item textures at 20x20 pixels each.
   * Each item has a distinctive shape and color accent.
   */
  _generateShopTextures() {
    this._generateThreiasGuideTexture();
    this._generateHeartPotionTexture();
    this._generateTotemVoraciousTexture();
    this._generateAgmuthPendantTexture();
    this._generateLavaRingTexture();
    this._generatePoisonRingTexture();
    this._generateWaterRingTexture();
    this._generateKeyPendantTexture();
    this._generateHeartPendantTexture();
    this._generateElementalRingTexture();
    this._generateTearOfThreiaTexture();
    this._generateHammerOfThreiaShopTexture();
  }

  /**
   * Threia's Guide: a scroll/parchment icon with a warm parchment tint.
   * Rectangular scroll shape with curled edges.
   */
  _generateThreiasGuideTexture() {
    const size = 20;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;

    // Parchment body
    g.fillStyle(COLOR_GUIDE_PARCHMENT, 0.9);
    g.fillRect(4, 3, 12, 14);

    // Scroll rolls (top and bottom)
    g.fillStyle(COLOR_GUIDE_PARCHMENT, 1);
    g.fillCircle(cx, 3, 2.5);
    g.fillCircle(cx, 17, 2.5);

    // Text lines on scroll
    g.lineStyle(0.8, 0x555555, 0.6);
    g.lineBetween(6, 6, 14, 6);
    g.lineBetween(6, 8, 13, 8);
    g.lineBetween(6, 10, 14, 10);
    g.lineBetween(6, 12, 11, 12);

    // Edge darkening
    g.lineStyle(0.5, 0x444444, 0.3);
    g.strokeRect(4, 3, 12, 14);

    g.generateTexture(TEX_SHOP_THREIAS_GUIDE, size, size);
    g.destroy();
  }

  /**
   * Heart Potion: a potion bottle shape with green tint.
   * Flask silhouette with a cork top.
   */
  _generateHeartPotionTexture() {
    const size = 20;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;

    // Bottle body (rounded bottom)
    g.fillStyle(COLOR_POTION_GREEN, 0.85);
    g.fillCircle(cx, 13, 5);
    g.fillRect(cx - 5, 8, 10, 5);

    // Bottle neck
    g.fillStyle(COLOR_POTION_GREEN, 0.7);
    g.fillRect(cx - 2, 4, 4, 5);

    // Cork
    g.fillStyle(0xaa8855, 0.9);
    g.fillRect(cx - 2.5, 2, 5, 3);

    // Liquid shine highlight
    g.fillStyle(0xffffff, 0.3);
    g.fillCircle(cx - 2, 11, 2);

    // Heart symbol on bottle
    g.fillStyle(0xffffff, 0.7);
    g.fillCircle(cx - 1.2, 12, 1.2);
    g.fillCircle(cx + 1.2, 12, 1.2);
    g.fillTriangle(cx - 2.5, 12.5, cx + 2.5, 12.5, cx, 15);

    g.generateTexture(TEX_SHOP_HEART_POTION, size, size);
    g.destroy();
  }

  /**
   * Totem of the Voracious: a square shape with a violet dot inside.
   * Represents a mystical totem that attracts food.
   */
  _generateTotemVoraciousTexture() {
    const size = 20;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;
    const cy = size / 2;

    // Outer square frame
    g.lineStyle(2, COLOR_WHITE, 0.8);
    g.strokeRect(3, 3, 14, 14);

    // Inner square
    g.lineStyle(1, COLOR_WHITE, 0.4);
    g.strokeRect(5, 5, 10, 10);

    // Violet center dot
    g.fillStyle(COLOR_VORACIOUS_VIOLET, 1);
    g.fillCircle(cx, cy, 3);

    // Glow around dot
    g.fillStyle(COLOR_VORACIOUS_VIOLET, 0.3);
    g.fillCircle(cx, cy, 5);

    // Small directional arrows pointing inward (attraction symbol)
    g.lineStyle(1, COLOR_WHITE, 0.6);
    // Top arrow
    g.lineBetween(cx, 4, cx, 6);
    // Bottom arrow
    g.lineBetween(cx, 16, cx, 14);
    // Left arrow
    g.lineBetween(4, cy, 6, cy);
    // Right arrow
    g.lineBetween(16, cy, 14, cy);

    g.generateTexture(TEX_SHOP_TOTEM_VORACIOUS, size, size);
    g.destroy();
  }

  /**
   * Agmuth Pendant: a pendant with a teardrop gem.
   * Chain/string at top with a violet teardrop gem hanging.
   */
  _generateAgmuthPendantTexture() {
    const size = 20;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;

    // Chain/cord (V-shape from top corners to pendant)
    g.lineStyle(1, COLOR_WHITE, 0.6);
    g.lineBetween(4, 2, cx, 7);
    g.lineBetween(16, 2, cx, 7);

    // Pendant frame (small circle)
    g.fillStyle(COLOR_WHITE, 0.3);
    g.fillCircle(cx, 10, 4.5);
    g.lineStyle(1.2, COLOR_WHITE, 0.8);
    g.strokeCircle(cx, 10, 4.5);

    // Teardrop gem
    g.fillStyle(COLOR_PENDANT_GEM, 1);
    g.fillCircle(cx, 9, 2);
    g.fillTriangle(cx - 2, 9.5, cx + 2, 9.5, cx, 14);

    // Gem highlight
    g.fillStyle(0xffffff, 0.4);
    g.fillCircle(cx - 0.5, 8.5, 0.8);

    g.generateTexture(TEX_SHOP_AGMUTH_PENDANT, size, size);
    g.destroy();
  }

  /**
   * Lava Ring: a ring with lava-red edges.
   * Simple ring shape with red/orange accent.
   */
  _generateLavaRingTexture() {
    const size = 20;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;
    const cy = size / 2;

    // Outer glow
    g.fillStyle(COLOR_LAVA_RING, 0.15);
    g.fillCircle(cx, cy, 8);

    // Ring band (outer)
    g.lineStyle(3, COLOR_LAVA_RING, 0.9);
    g.strokeCircle(cx, cy, 6);

    // Ring band (inner highlight)
    g.lineStyle(1.5, 0xff7744, 0.6);
    g.strokeCircle(cx, cy, 5);

    // Central gem on ring
    g.fillStyle(0xff2200, 1);
    g.fillCircle(cx, cy - 5, 2);
    g.fillStyle(0xffffff, 0.4);
    g.fillCircle(cx - 0.3, cy - 5.5, 0.7);

    g.generateTexture(TEX_SHOP_LAVA_RING, size, size);
    g.destroy();
  }

  /**
   * Poison Ring: a ring with poison-green edges.
   */
  _generatePoisonRingTexture() {
    const size = 20;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;
    const cy = size / 2;

    // Outer glow
    g.fillStyle(COLOR_POISON_RING, 0.15);
    g.fillCircle(cx, cy, 8);

    // Ring band (outer)
    g.lineStyle(3, COLOR_POISON_RING, 0.9);
    g.strokeCircle(cx, cy, 6);

    // Ring band (inner highlight)
    g.lineStyle(1.5, 0x44ee66, 0.6);
    g.strokeCircle(cx, cy, 5);

    // Central gem on ring
    g.fillStyle(0x00aa22, 1);
    g.fillCircle(cx, cy - 5, 2);
    g.fillStyle(0xffffff, 0.4);
    g.fillCircle(cx - 0.3, cy - 5.5, 0.7);

    g.generateTexture(TEX_SHOP_POISON_RING, size, size);
    g.destroy();
  }

  /**
   * Water Ring: a ring with blue edges.
   */
  _generateWaterRingTexture() {
    const size = 20;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;
    const cy = size / 2;

    // Outer glow
    g.fillStyle(COLOR_WATER_RING, 0.15);
    g.fillCircle(cx, cy, 8);

    // Ring band (outer)
    g.lineStyle(3, COLOR_WATER_RING, 0.9);
    g.strokeCircle(cx, cy, 6);

    // Ring band (inner highlight)
    g.lineStyle(1.5, 0x66aaff, 0.6);
    g.strokeCircle(cx, cy, 5);

    // Central gem on ring
    g.fillStyle(0x2266cc, 1);
    g.fillCircle(cx, cy - 5, 2);
    g.fillStyle(0xffffff, 0.4);
    g.fillCircle(cx - 0.3, cy - 5.5, 0.7);

    g.generateTexture(TEX_SHOP_WATER_RING, size, size);
    g.destroy();
  }

  /**
   * Key Pendant: a pendant with a small key dangling.
   * Silver chain with a miniature key shape.
   */
  _generateKeyPendantTexture() {
    const size = 20;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;

    // Chain/cord
    g.lineStyle(1, COLOR_KEY_PENDANT, 0.6);
    g.lineBetween(5, 2, cx, 6);
    g.lineBetween(15, 2, cx, 6);

    // Pendant frame (oval)
    g.fillStyle(COLOR_KEY_PENDANT, 0.2);
    g.fillEllipse(cx, 11, 10, 12);
    g.lineStyle(1, COLOR_KEY_PENDANT, 0.7);
    g.strokeEllipse(cx, 11, 10, 12);

    // Mini key shape inside
    // Key head ring
    g.fillStyle(COLOR_KEY_PENDANT, 1);
    g.fillCircle(cx, 8, 2.5);
    g.fillStyle(0x000000, 1);
    g.fillCircle(cx, 8, 1);

    // Key shaft
    g.fillStyle(COLOR_KEY_PENDANT, 1);
    g.fillRect(cx - 0.7, 10, 1.5, 5);

    // Key teeth
    g.fillRect(cx + 0.8, 13, 1.5, 1);
    g.fillRect(cx + 0.8, 14.5, 1, 1);

    g.generateTexture(TEX_SHOP_KEY_PENDANT, size, size);
    g.destroy();
  }

  /**
   * Heart Pendant: a pendant with a reddish heart gem.
   * Chain/string at top with a small heart shape hanging.
   */
  _generateHeartPendantTexture() {
    const size = 20;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;

    // Chain/cord (V-shape from top corners to pendant)
    g.lineStyle(1, COLOR_WHITE, 0.6);
    g.lineBetween(4, 2, cx, 6);
    g.lineBetween(16, 2, cx, 6);

    // Pendant frame (small circle)
    g.fillStyle(COLOR_WHITE, 0.25);
    g.fillCircle(cx, 10, 5);
    g.lineStyle(1.2, COLOR_WHITE, 0.7);
    g.strokeCircle(cx, 10, 5);

    // Heart shape (two overlapping circles + triangle)
    g.fillStyle(COLOR_HEART_PENDANT, 1);
    g.fillCircle(cx - 1.5, 9, 2);
    g.fillCircle(cx + 1.5, 9, 2);
    g.fillTriangle(cx - 3.2, 10, cx + 3.2, 10, cx, 14);

    // Heart highlight
    g.fillStyle(0xffffff, 0.35);
    g.fillCircle(cx - 1.5, 8.3, 0.8);

    g.generateTexture(TEX_SHOP_HEART_PENDANT, size, size);
    g.destroy();
  }

  /**
   * Elemental Ring: a muted silver ring with three small colored gems
   * (lava red-orange, poison green, water blue) evenly spaced around the top.
   * Subdued palette — the gems are small accents, not overwhelming.
   */
  _generateElementalRingTexture() {
    const size = 20;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;
    const cy = size / 2;

    // Subtle combined-element outer glow (muted silver-blue)
    g.fillStyle(COLOR_ELEMENTAL_RING, 0.12);
    g.fillCircle(cx, cy, 8);

    // Ring band (outer) — silver/gray base
    g.lineStyle(3, COLOR_ELEMENTAL_RING, 0.85);
    g.strokeCircle(cx, cy, 6);

    // Ring band (inner highlight) — lighter silver
    g.lineStyle(1.5, 0xbbccdd, 0.5);
    g.strokeCircle(cx, cy, 5);

    // Three gem positions — arranged along the top arc of the ring
    // Left gem (water blue), center gem (lava red-orange), right gem (poison green)
    const gemR = 1.5;
    const gemDist = 6; // radius from center to gem position

    // Water gem — upper-left (approx -135° on the ring)
    const waterAngle = -Math.PI * 0.75;
    const wxg = cx + Math.cos(waterAngle) * gemDist;
    const wyg = cy + Math.sin(waterAngle) * gemDist;
    g.fillStyle(COLOR_WATER_RING, 0.9);
    g.fillCircle(wxg, wyg, gemR);
    g.fillStyle(0xffffff, 0.3);
    g.fillCircle(wxg - 0.3, wyg - 0.3, 0.5);

    // Lava gem — top-center (approx -90° on the ring)
    const lavaAngle = -Math.PI * 0.5;
    const lxg = cx + Math.cos(lavaAngle) * gemDist;
    const lyg = cy + Math.sin(lavaAngle) * gemDist;
    g.fillStyle(COLOR_LAVA_RING, 0.9);
    g.fillCircle(lxg, lyg, gemR);
    g.fillStyle(0xffffff, 0.3);
    g.fillCircle(lxg - 0.3, lyg - 0.3, 0.5);

    // Poison gem — upper-right (approx -45° on the ring)
    const poisonAngle = -Math.PI * 0.25;
    const pxg = cx + Math.cos(poisonAngle) * gemDist;
    const pyg = cy + Math.sin(poisonAngle) * gemDist;
    g.fillStyle(COLOR_POISON_RING, 0.9);
    g.fillCircle(pxg, pyg, gemR);
    g.fillStyle(0xffffff, 0.3);
    g.fillCircle(pxg - 0.3, pyg - 0.3, 0.5);

    g.generateTexture(TEX_SHOP_ELEMENTAL_RING, size, size);
    g.destroy();
  }

  /**
   * Tear of Threia: a teardrop shape with light blue outer shell and
   * a golden interior glow. Represents a divine tear that prevents
   * game over by resurrecting the player.
   */
  _generateTearOfThreiaTexture() {
    const size = 20;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;

    // Soft outer glow (celestial blue halo)
    g.fillStyle(COLOR_TEAR_THREIA_BLUE, 0.12);
    g.fillCircle(cx, 12, 8);

    // Teardrop body — light blue outer shell
    // Drawn as a combination of a circle (bottom) and triangle (top point)
    g.fillStyle(COLOR_TEAR_THREIA_BLUE, 0.85);
    // Bottom rounded part
    g.fillCircle(cx, 13, 5);
    // Top point — triangle narrowing to a tip
    g.fillTriangle(cx, 2, cx - 5, 13, cx + 5, 13);

    // Inner golden glow — smaller teardrop
    g.fillStyle(COLOR_TEAR_THREIA_GOLD, 0.7);
    g.fillCircle(cx, 13, 3);
    g.fillTriangle(cx, 5, cx - 3, 13, cx + 3, 13);

    // Bright core highlight — white sparkle near top
    g.fillStyle(0xffffff, 0.6);
    g.fillCircle(cx - 1, 9, 1.2);

    // Small white specular at bottom
    g.fillStyle(0xffffff, 0.35);
    g.fillCircle(cx + 1, 14, 0.8);

    g.generateTexture(TEX_SHOP_TEAR_OF_THREIA, size, size);
    g.destroy();
  }

  // ---------------------------------------------------------------
  // Bat textures (two wing animation frames)
  // ---------------------------------------------------------------

  /**
   * Generate two bat textures: wings-up and wings-down.
   * Small ~14px sprites — black body, two red eyes, spread wings.
   * Coherent with the retro monochrome aesthetic.
   */
  _generateBatTextures() {
    const size = BAT_SIZE;
    const cx = size / 2;
    const cy = size / 2;

    // --- Wings Up frame ---
    const g1 = this.make.graphics({ x: 0, y: 0, add: false });

    // Outline pass — light gray border drawn slightly thicker behind fills
    const outlineColor = 0xaaaaaa;
    const outlineAlpha = 0.85;

    // Body outline
    g1.lineStyle(1.5, outlineColor, outlineAlpha);
    g1.strokeEllipse(cx, cy, 5, 4);

    // Left wing outline
    g1.lineStyle(1, outlineColor, outlineAlpha);
    g1.beginPath();
    g1.moveTo(cx - 2, cy);
    g1.lineTo(cx - 7, cy - 5);
    g1.lineTo(cx - 6, cy + 1);
    g1.closePath();
    g1.strokePath();

    // Right wing outline
    g1.beginPath();
    g1.moveTo(cx + 2, cy);
    g1.lineTo(cx + 7, cy - 5);
    g1.lineTo(cx + 6, cy + 1);
    g1.closePath();
    g1.strokePath();

    // Ear outlines
    g1.beginPath();
    g1.moveTo(cx - 2, cy - 2);
    g1.lineTo(cx - 1, cy - 4);
    g1.lineTo(cx, cy - 2);
    g1.closePath();
    g1.strokePath();
    g1.beginPath();
    g1.moveTo(cx + 2, cy - 2);
    g1.lineTo(cx + 1, cy - 4);
    g1.lineTo(cx, cy - 2);
    g1.closePath();
    g1.strokePath();

    // Fill pass — body
    g1.fillStyle(0x111111, 1);
    g1.fillEllipse(cx, cy, 5, 4);

    // Left wing — angled upward
    g1.fillStyle(0x222222, 1);
    g1.fillTriangle(
      cx - 2, cy,        // inner attachment point
      cx - 7, cy - 5,    // wing tip (upper-left)
      cx - 6, cy + 1,    // wing trailing edge
    );
    // Small wing membrane detail
    g1.fillTriangle(
      cx - 5, cy - 3,
      cx - 7, cy - 5,
      cx - 3, cy - 1,
    );

    // Right wing — angled upward (mirror)
    g1.fillTriangle(
      cx + 2, cy,
      cx + 7, cy - 5,
      cx + 6, cy + 1,
    );
    g1.fillTriangle(
      cx + 5, cy - 3,
      cx + 7, cy - 5,
      cx + 3, cy - 1,
    );

    // Ears (two small triangles on top of head)
    g1.fillStyle(0x111111, 1);
    g1.fillTriangle(cx - 2, cy - 2, cx - 1, cy - 4, cx, cy - 2);
    g1.fillTriangle(cx + 2, cy - 2, cx + 1, cy - 4, cx, cy - 2);

    // Eyes (two red dots)
    g1.fillStyle(COLOR_BAT_EYES, 1);
    g1.fillCircle(cx - 1.2, cy - 0.5, 0.8);
    g1.fillCircle(cx + 1.2, cy - 0.5, 0.8);

    g1.generateTexture(TEX_BAT_WINGS_UP, size, size);
    g1.destroy();

    // --- Wings Down frame ---
    const g2 = this.make.graphics({ x: 0, y: 0, add: false });

    // Outline pass — light gray border
    g2.lineStyle(1.5, outlineColor, outlineAlpha);
    g2.strokeEllipse(cx, cy, 5, 4);

    g2.lineStyle(1, outlineColor, outlineAlpha);
    // Left wing outline
    g2.beginPath();
    g2.moveTo(cx - 2, cy);
    g2.lineTo(cx - 7, cy + 3);
    g2.lineTo(cx - 5, cy - 1);
    g2.closePath();
    g2.strokePath();

    // Right wing outline
    g2.beginPath();
    g2.moveTo(cx + 2, cy);
    g2.lineTo(cx + 7, cy + 3);
    g2.lineTo(cx + 5, cy - 1);
    g2.closePath();
    g2.strokePath();

    // Ear outlines
    g2.beginPath();
    g2.moveTo(cx - 2, cy - 2);
    g2.lineTo(cx - 1, cy - 4);
    g2.lineTo(cx, cy - 2);
    g2.closePath();
    g2.strokePath();
    g2.beginPath();
    g2.moveTo(cx + 2, cy - 2);
    g2.lineTo(cx + 1, cy - 4);
    g2.lineTo(cx, cy - 2);
    g2.closePath();
    g2.strokePath();

    // Fill pass — body
    g2.fillStyle(0x111111, 1);
    g2.fillEllipse(cx, cy, 5, 4);

    // Left wing — angled downward
    g2.fillStyle(0x222222, 1);
    g2.fillTriangle(
      cx - 2, cy,        // inner attachment
      cx - 7, cy + 3,    // wing tip (lower-left)
      cx - 5, cy - 1,    // wing leading edge
    );
    g2.fillTriangle(
      cx - 4, cy + 1,
      cx - 7, cy + 3,
      cx - 2, cy + 2,
    );

    // Right wing — angled downward (mirror)
    g2.fillTriangle(
      cx + 2, cy,
      cx + 7, cy + 3,
      cx + 5, cy - 1,
    );
    g2.fillTriangle(
      cx + 4, cy + 1,
      cx + 7, cy + 3,
      cx + 2, cy + 2,
    );

    // Ears
    g2.fillStyle(0x111111, 1);
    g2.fillTriangle(cx - 2, cy - 2, cx - 1, cy - 4, cx, cy - 2);
    g2.fillTriangle(cx + 2, cy - 2, cx + 1, cy - 4, cx, cy - 2);

    // Eyes
    g2.fillStyle(COLOR_BAT_EYES, 1);
    g2.fillCircle(cx - 1.2, cy - 0.5, 0.8);
    g2.fillCircle(cx + 1.2, cy - 0.5, 0.8);

    g2.generateTexture(TEX_BAT_WINGS_DOWN, size, size);
    g2.destroy();
  }

  /**
   * Manuscript: an 18×18 aged parchment scroll on the maze floor.
   * Similar to Threia's Guide but slightly smaller and with a distinct look —
   * rolled edges, faded text lines, and a warmer parchment tone.
   */
  _generateManuscriptTexture() {
    const size = 18;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;

    // Parchment body
    g.fillStyle(COLOR_MANUSCRIPT_PARCHMENT, 0.85);
    g.fillRect(3, 3, 12, 12);

    // Scroll rolls (top and bottom)
    g.fillStyle(COLOR_MANUSCRIPT_PARCHMENT, 1);
    g.fillCircle(cx, 3, 2);
    g.fillCircle(cx, 15, 2);

    // Text lines on scroll (faded writing)
    g.lineStyle(0.7, 0x666666, 0.5);
    g.lineBetween(5, 5.5, 13, 5.5);
    g.lineBetween(5, 7.5, 12, 7.5);
    g.lineBetween(5, 9.5, 13, 9.5);
    g.lineBetween(5, 11.5, 10, 11.5);

    // Edge darkening (aged look)
    g.lineStyle(0.5, 0x555555, 0.25);
    g.strokeRect(3, 3, 12, 12);

    // Small wax seal dot (bottom-right corner, reddish)
    g.fillStyle(0x993333, 0.6);
    g.fillCircle(12, 13, 1.3);

    g.generateTexture(TEX_MANUSCRIPT, size, size);
    g.destroy();
  }

  /**
   * Generate the Hammer of Threia floor pickup texture (18×18).
   * A Mjölnir-style hammer with celestial blue handle and gold head,
   * matching the Threia divine item aesthetic.
   */
  _generateHammerOfThreiaFloorTexture() {
    const size = 18;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;

    // Soft celestial halo
    g.fillStyle(COLOR_HAMMER_BLUE, 0.10);
    g.fillCircle(cx, cx, size / 2);

    // Handle — vertical shaft (celestial blue)
    g.fillStyle(COLOR_HAMMER_BLUE, 0.85);
    g.fillRect(cx - 1.2, 6, 2.4, 10);

    // Handle grip wrapping (darker lines)
    g.lineStyle(0.6, COLOR_HAMMER_BLUE, 0.5);
    for (let y = 10; y <= 15; y += 1.5) {
      g.beginPath();
      g.moveTo(cx - 1.5, y);
      g.lineTo(cx + 1.5, y);
      g.strokePath();
    }

    // Hammer head — wide rectangular block (gold)
    g.fillStyle(COLOR_HAMMER_GOLD, 0.9);
    g.fillRect(cx - 5, 2, 10, 5);

    // Hammer head top bevel (brighter gold highlight)
    g.fillStyle(COLOR_HAMMER_GOLD, 1.0);
    g.fillRect(cx - 5, 2, 10, 1.2);

    // Hammer head side faces (darker gold for depth)
    g.fillStyle(COLOR_HAMMER_GOLD, 0.65);
    g.fillRect(cx - 5, 5.5, 1.2, 1.5);
    g.fillRect(cx + 3.8, 5.5, 1.2, 1.5);

    // Head-to-handle junction
    g.fillStyle(COLOR_HAMMER_BLUE, 0.7);
    g.fillRect(cx - 2, 5.5, 4, 1.5);

    // Divine sparkle at top center
    g.fillStyle(0xffffff, 0.7);
    g.fillCircle(cx, 3, 0.8);

    // Small specular highlight on head
    g.fillStyle(0xffffff, 0.35);
    g.fillCircle(cx - 2.5, 3.5, 0.5);

    g.generateTexture(TEX_HAMMER_OF_THREIA, size, size);
    g.destroy();
  }

  /**
   * Generate the wall debris/rubble texture (24×24 = TILE_SIZE).
   * Shown where a wall was broken by the hammer. Scattered stone
   * fragments in grays on a dark background.
   */
  _generateWallDebrisTexture() {
    const size = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Dark ground base
    g.fillStyle(0x111111, 1);
    g.fillRect(0, 0, size, size);

    // Scattered rubble fragments — varying sizes and gray shades
    const fragments = [
      { x: 3, y: 4, w: 5, h: 3, color: 0x555555, alpha: 0.7 },
      { x: 14, y: 2, w: 4, h: 4, color: 0x444444, alpha: 0.6 },
      { x: 8, y: 8, w: 6, h: 3, color: 0x666666, alpha: 0.5 },
      { x: 2, y: 14, w: 3, h: 4, color: 0x555555, alpha: 0.55 },
      { x: 17, y: 12, w: 5, h: 3, color: 0x4a4a4a, alpha: 0.6 },
      { x: 10, y: 16, w: 4, h: 5, color: 0x3a3a3a, alpha: 0.5 },
      { x: 19, y: 19, w: 3, h: 3, color: 0x505050, alpha: 0.45 },
      { x: 6, y: 20, w: 5, h: 2, color: 0x484848, alpha: 0.5 },
    ];

    for (const f of fragments) {
      g.fillStyle(f.color, f.alpha);
      g.fillRect(f.x, f.y, f.w, f.h);
    }

    // Small dust particles
    g.fillStyle(0x777777, 0.3);
    g.fillCircle(12, 5, 1);
    g.fillCircle(5, 11, 0.8);
    g.fillCircle(20, 8, 0.7);
    g.fillCircle(15, 20, 0.9);

    g.generateTexture(TEX_WALL_DEBRIS, size, size);
    g.destroy();
  }

  /**
   * Generate the Hammer of Threia shop icon texture (20×20).
   * A cleaner, more iconographic version of the floor hammer with
   * celestial blue and gold accent — matches shop icon style.
   */
  _generateHammerOfThreiaShopTexture() {
    const size = 20;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = size / 2;

    // Soft outer glow (celestial blue halo)
    g.fillStyle(COLOR_HAMMER_BLUE, 0.12);
    g.fillCircle(cx, cx, 9);

    // Handle — vertical shaft (celestial blue)
    g.fillStyle(COLOR_HAMMER_BLUE, 0.85);
    g.fillRect(cx - 1.3, 7, 2.6, 11);

    // Handle grip detail
    g.lineStyle(0.5, COLOR_HAMMER_BLUE, 0.45);
    for (let y = 11; y <= 16; y += 1.5) {
      g.beginPath();
      g.moveTo(cx - 1.6, y);
      g.lineTo(cx + 1.6, y);
      g.strokePath();
    }

    // Hammer head — gold block
    g.fillStyle(COLOR_HAMMER_GOLD, 0.9);
    g.fillRect(cx - 6, 2, 12, 6);

    // Top bevel highlight
    g.fillStyle(COLOR_HAMMER_GOLD, 1.0);
    g.fillRect(cx - 6, 2, 12, 1.5);

    // Side shadow faces
    g.fillStyle(COLOR_HAMMER_GOLD, 0.6);
    g.fillRect(cx - 6, 6.5, 1.5, 1.5);
    g.fillRect(cx + 4.5, 6.5, 1.5, 1.5);

    // Junction block
    g.fillStyle(COLOR_HAMMER_BLUE, 0.65);
    g.fillRect(cx - 2.5, 6, 5, 2);

    // Divine sparkle
    g.fillStyle(0xffffff, 0.7);
    g.fillCircle(cx, 3.5, 1);

    // Specular highlights
    g.fillStyle(0xffffff, 0.35);
    g.fillCircle(cx - 3, 4, 0.6);
    g.fillCircle(cx + 2, 4.5, 0.4);

    g.generateTexture(TEX_SHOP_HAMMER_OF_THREIA, size, size);
    g.destroy();
  }

  // ---------------------------------------------------------------
  // Final Battle texture generators
  // ---------------------------------------------------------------

  /**
   * Celestial Exit: white glowing portal with golden/orange edges.
   * Materializes when the player has all 4 Agmuth relics.
   */
  _generateCelestialExitTexture() {
    const s = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = s / 2;
    const cy = s / 2;
    const r = s * 0.42;

    // Outer golden-orange glow halo
    g.fillStyle(COLOR_CELESTIAL_ORANGE, 0.15);
    g.fillCircle(cx, cy, r + 3);
    g.fillStyle(COLOR_CELESTIAL_GOLD, 0.2);
    g.fillCircle(cx, cy, r + 1.5);

    // Golden edge ring
    g.lineStyle(2.5, COLOR_CELESTIAL_GOLD, 0.85);
    g.strokeCircle(cx, cy, r);

    // Inner orange accent ring
    g.lineStyle(1.2, COLOR_CELESTIAL_ORANGE, 0.6);
    g.strokeCircle(cx, cy, r - 2);

    // White core fill
    g.fillStyle(COLOR_CELESTIAL_EXIT, 0.9);
    g.fillCircle(cx, cy, r - 3);

    // Central bright spot
    g.fillStyle(0xffffff, 1.0);
    g.fillCircle(cx, cy, r * 0.4);

    // Radial light rays (4 cardinal + 4 diagonal)
    g.lineStyle(0.8, 0xffffff, 0.5);
    for (let a = 0; a < 8; a++) {
      const angle = (a * Math.PI) / 4;
      const ir = r * 0.25;
      const or = r - 1;
      g.beginPath();
      g.moveTo(cx + Math.cos(angle) * ir, cy + Math.sin(angle) * ir);
      g.lineTo(cx + Math.cos(angle) * or, cy + Math.sin(angle) * or);
      g.strokePath();
    }

    // Golden sparkle dots at edge
    g.fillStyle(COLOR_CELESTIAL_GOLD, 0.9);
    g.fillCircle(cx, cy - r + 1, 1.2);
    g.fillCircle(cx + r - 1, cy, 1.0);
    g.fillCircle(cx, cy + r - 1, 1.2);
    g.fillCircle(cx - r + 1, cy, 1.0);

    g.generateTexture(TEX_CELESTIAL_EXIT, s, s);
    g.destroy();
  }

  /**
   * Vorath Mass: an enormous, deformed, twisted dark organism that has consumed
   * countless souls and energy, growing into a grotesque asymmetric mass.
   * Blood-red edges, orange veins, pulsing red core. 192px sprite.
   */
  _generateVorathMassTexture() {
    const s = FINAL_VORATH_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = s / 2;
    const cy = s / 2;

    // ── Irregular silhouette from dozens of overlapping blobs ──
    // The mass has consumed so many souls it grew deformed and enormous.
    // No symmetry — each side bulges differently.

    const blobs = [
      // Core body — large overlapping masses, deliberately off-center
      { x: cx - 6, y: cy + 4, w: 100, h: 86 },
      { x: cx + 8, y: cy - 6, w: 90, h: 98 },
      { x: cx - 2, y: cy, w: 80, h: 82 },
      { x: cx + 4, y: cy + 8, w: 76, h: 70 },

      // Major protrusions — large asymmetric growths
      { x: cx - 48, y: cy - 16, w: 52, h: 36 },   // left upper growth
      { x: cx + 44, y: cy + 20, w: 44, h: 42 },   // right lower growth
      { x: cx + 16, y: cy - 44, w: 36, h: 48 },   // top right mass
      { x: cx - 24, y: cy + 40, w: 48, h: 32 },   // bottom left mass
      { x: cx + 36, y: cy - 28, w: 32, h: 40 },   // upper right lump
      { x: cx - 40, y: cy + 16, w: 40, h: 28 },   // left side growth
      { x: cx - 12, y: cy - 36, w: 44, h: 28 },   // top bulge
      { x: cx + 8, y: cy + 44, w: 36, h: 32 },    // bottom right growth

      // Secondary bulges — medium-sized deformities
      { x: cx - 56, y: cy - 4, w: 32, h: 24 },    // far left
      { x: cx + 56, y: cy + 6, w: 28, h: 22 },    // far right
      { x: cx - 16, y: cy + 56, w: 28, h: 24 },   // far bottom
      { x: cx + 28, y: cy - 52, w: 22, h: 28 },   // far top
      { x: cx - 36, y: cy - 34, w: 30, h: 26 },   // upper-left
      { x: cx + 48, y: cy - 10, w: 26, h: 32 },   // right nodule
      { x: cx - 50, y: cy + 30, w: 24, h: 22 },   // lower-left
      { x: cx + 38, y: cy + 38, w: 22, h: 24 },   // lower-right

      // Tendrils — elongated small blobs at the edges
      { x: cx - 64, y: cy - 8, w: 22, h: 14 },    // left tendril
      { x: cx + 62, y: cy + 14, w: 18, h: 14 },   // right tendril
      { x: cx - 10, y: cy + 64, w: 18, h: 16 },   // bottom tendril
      { x: cx + 18, y: cy - 62, w: 14, h: 18 },   // top tendril
      { x: cx - 58, y: cy - 24, w: 16, h: 12 },   // upper-left tendril
      { x: cx + 54, y: cy - 22, w: 14, h: 14 },   // upper-right tendril
      { x: cx - 44, y: cy + 44, w: 16, h: 12 },   // lower-left tendril
      { x: cx + 52, y: cy + 32, w: 14, h: 16 },   // lower-right tendril

      // Tiny nodules — small bumps that make edges feel organic
      { x: cx - 70, y: cy + 4, w: 12, h: 10 },
      { x: cx + 68, y: cy - 2, w: 10, h: 12 },
      { x: cx + 6, y: cy + 70, w: 12, h: 10 },
      { x: cx - 4, y: cy - 68, w: 10, h: 14 },
      { x: cx - 62, y: cy + 18, w: 10, h: 10 },
      { x: cx + 60, y: cy - 16, w: 10, h: 10 },
    ];

    // Layer 1: Outer dark-red aura (faint glow around the mass)
    g.fillStyle(COLOR_VORATH_RED, 0.04);
    for (const b of blobs) {
      g.fillEllipse(b.x, b.y, b.w + 20, b.h + 20);
    }
    g.fillStyle(COLOR_VORATH_RED, 0.08);
    for (const b of blobs) {
      g.fillEllipse(b.x, b.y, b.w + 10, b.h + 10);
    }

    // Layer 2: Blood-red edge outlines (irregular, following blob contours)
    g.lineStyle(3, COLOR_VORATH_RED, 0.6);
    for (const b of blobs) {
      g.strokeEllipse(b.x, b.y, b.w + 4, b.h + 4);
    }

    // Layer 3: Near-black body fill
    g.fillStyle(COLOR_VORATH_DARK, 1.0);
    for (const b of blobs) {
      g.fillEllipse(b.x, b.y, b.w, b.h);
    }

    // Layer 4: Inner dark depth patches (pseudo 3D)
    g.fillStyle(0x080000, 0.8);
    g.fillEllipse(cx - 16, cy - 12, 60, 50);
    g.fillEllipse(cx + 12, cy + 8, 50, 44);
    g.fillEllipse(cx - 4, cy + 16, 40, 36);
    g.fillEllipse(cx + 20, cy - 20, 36, 40);

    // Layer 5: Deep core — almost pure black center
    g.fillStyle(0x030000, 1.0);
    g.fillEllipse(cx - 4, cy + 2, 44, 36);
    g.fillEllipse(cx + 6, cy - 6, 32, 40);

    // Layer 6: Orange vein streaks — thick, branching, organic
    g.lineStyle(2.5, COLOR_VORATH_ORANGE, 0.5);
    // Major veins radiating from center
    g.beginPath(); g.moveTo(cx - 50, cy - 24); g.lineTo(cx - 28, cy - 8); g.lineTo(cx - 12, cy + 4); g.strokePath();
    g.beginPath(); g.moveTo(cx + 44, cy - 16); g.lineTo(cx + 24, cy + 4); g.lineTo(cx + 12, cy + 28); g.strokePath();
    g.beginPath(); g.moveTo(cx - 32, cy + 28); g.lineTo(cx - 8, cy + 40); g.lineTo(cx + 16, cy + 36); g.strokePath();
    g.beginPath(); g.moveTo(cx + 8, cy - 40); g.lineTo(cx + 20, cy - 20); g.lineTo(cx + 14, cy + 4); g.strokePath();
    g.beginPath(); g.moveTo(cx - 44, cy + 8); g.lineTo(cx - 22, cy + 2); g.lineTo(cx - 8, cy - 10); g.strokePath();
    g.beginPath(); g.moveTo(cx + 36, cy + 24); g.lineTo(cx + 16, cy + 12); g.lineTo(cx - 4, cy + 8); g.strokePath();
    // Forking veins
    g.lineStyle(1.8, COLOR_VORATH_ORANGE, 0.4);
    g.beginPath(); g.moveTo(cx - 28, cy - 8); g.lineTo(cx - 36, cy + 4); g.strokePath();
    g.beginPath(); g.moveTo(cx + 24, cy + 4); g.lineTo(cx + 36, cy + 10); g.strokePath();
    g.beginPath(); g.moveTo(cx + 20, cy - 20); g.lineTo(cx + 32, cy - 28); g.strokePath();
    g.beginPath(); g.moveTo(cx - 8, cy + 40); g.lineTo(cx - 18, cy + 50); g.strokePath();

    // Secondary thinner veins
    g.lineStyle(1.2, COLOR_VORATH_ORANGE, 0.3);
    g.beginPath(); g.moveTo(cx - 40, cy - 12); g.lineTo(cx - 20, cy - 4); g.lineTo(cx, cy - 16); g.strokePath();
    g.beginPath(); g.moveTo(cx + 32, cy + 24); g.lineTo(cx + 16, cy + 12); g.lineTo(cx - 4, cy + 8); g.strokePath();
    g.beginPath(); g.moveTo(cx - 8, cy - 32); g.lineTo(cx - 16, cy - 12); g.strokePath();
    g.beginPath(); g.moveTo(cx + 52, cy + 4); g.lineTo(cx + 32, cy - 4); g.strokePath();
    g.beginPath(); g.moveTo(cx - 52, cy + 20); g.lineTo(cx - 30, cy + 14); g.strokePath();
    g.beginPath(); g.moveTo(cx + 10, cy + 52); g.lineTo(cx + 4, cy + 30); g.strokePath();

    // Layer 7: Red vein network — radiating capillaries
    g.lineStyle(1.0, COLOR_VORATH_RED, 0.4);
    const veinAngles = [0.15, 0.55, 0.95, 1.35, 1.75, 2.15, 2.6, 3.0, 3.4, 3.8, 4.2, 4.65, 5.1, 5.5, 5.9];
    for (const a of veinAngles) {
      const innerR = 10 + Math.random() * 8;
      const outerR = 40 + Math.random() * 28;
      const mx = cx + Math.cos(a + 0.2) * (innerR + outerR) * 0.45;
      const my = cy + Math.sin(a - 0.1) * (innerR + outerR) * 0.45;
      g.beginPath();
      g.moveTo(cx + Math.cos(a) * innerR, cy + Math.sin(a) * innerR);
      g.lineTo(mx, my);
      g.lineTo(cx + Math.cos(a) * outerR, cy + Math.sin(a) * outerR);
      g.strokePath();
    }

    // Layer 8: Surface pockmarks — scattered dark craters
    g.fillStyle(0x0a0000, 0.6);
    const pockmarks = [
      [cx - 28, cy - 20, 4], [cx + 20, cy - 24, 3.5], [cx + 36, cy + 12, 4],
      [cx - 36, cy + 12, 3.5], [cx + 8, cy + 32, 4], [cx - 20, cy + 28, 4.5],
      [cx - 44, cy - 8, 3], [cx + 28, cy - 8, 3.5], [cx - 8, cy - 28, 4],
      [cx + 40, cy - 20, 3], [cx - 12, cy + 48, 2.5], [cx + 24, cy + 32, 3],
      [cx - 52, cy + 4, 2.5], [cx + 48, cy - 4, 2.5], [cx - 30, cy - 30, 3],
      [cx + 30, cy + 40, 2.5], [cx - 20, cy + 44, 3], [cx + 44, cy + 22, 2.5],
    ];
    for (const [px, py, pr] of pockmarks) {
      g.fillCircle(px, py, pr);
    }

    // Layer 9: Glowing red eyes — large, menacing, asymmetric
    // Left eye (larger, slightly higher)
    g.fillStyle(0x990000, 0.6);
    g.fillCircle(cx - 18, cy - 14, 9);
    g.fillStyle(0xcc0000, 0.7);
    g.fillCircle(cx - 18, cy - 14, 7);
    g.fillStyle(0xff0000, 0.9);
    g.fillCircle(cx - 18, cy - 14, 5);
    g.fillStyle(0xff4444, 0.7);
    g.fillCircle(cx - 19, cy - 16, 2.5);

    // Right eye (slightly smaller, different height — asymmetric face)
    g.fillStyle(0x990000, 0.6);
    g.fillCircle(cx + 16, cy - 10, 8);
    g.fillStyle(0xcc0000, 0.7);
    g.fillCircle(cx + 16, cy - 10, 6);
    g.fillStyle(0xff0000, 0.9);
    g.fillCircle(cx + 16, cy - 10, 4);
    g.fillStyle(0xff4444, 0.7);
    g.fillCircle(cx + 17, cy - 12, 2);

    // Third eye — barely visible, half-formed (consumed souls)
    g.fillStyle(0x660000, 0.4);
    g.fillCircle(cx - 2, cy - 24, 5);
    g.fillStyle(0x990000, 0.5);
    g.fillCircle(cx - 2, cy - 24, 3);

    g.generateTexture(TEX_VORATH_MASS, s, s);
    g.destroy();
  }

  /**
   * Altar of Threia: majestic 48px altar sprite occupying 2×2 tiles.
   * Multi-tiered temple structure with angel wings, celestial gems,
   * ornate pedestal, and golden crown. Placed at the 4 maze corners
   * during the final battle.
   */
  _generateAltarThreiaTexture() {
    const s = FINAL_ALTAR_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = s / 2;  // 24
    const cy = s / 2;  // 24

    // ── Outer celestial aura (large soft glow) ──
    g.fillStyle(COLOR_ALTAR_CELESTIAL, 0.06);
    g.fillCircle(cx, cy, s * 0.48);
    g.fillStyle(COLOR_ALTAR_GOLD, 0.08);
    g.fillCircle(cx, cy, s * 0.40);
    g.fillStyle(COLOR_ALTAR_CELESTIAL, 0.05);
    g.fillCircle(cx, cy, s * 0.33);

    // ── Left wing — large curved shape with multiple feathers ──
    g.fillStyle(COLOR_ALTAR_CELESTIAL, 0.55);
    g.fillTriangle(
      cx - 4, cy - 4,    // wing root top
      cx - 22, cy - 16,  // wing tip
      cx - 18, cy + 2    // wing bottom
    );
    // Inner wing layer
    g.fillStyle(COLOR_ALTAR_CELESTIAL, 0.4);
    g.fillTriangle(
      cx - 5, cy - 2,
      cx - 20, cy - 10,
      cx - 16, cy + 4
    );
    // Feather strokes
    g.lineStyle(0.6, COLOR_ALTAR_GOLD, 0.45);
    g.beginPath();
    g.moveTo(cx - 6, cy - 3);
    g.lineTo(cx - 19, cy - 12);
    g.strokePath();
    g.beginPath();
    g.moveTo(cx - 6, cy - 1);
    g.lineTo(cx - 17, cy - 7);
    g.strokePath();
    g.beginPath();
    g.moveTo(cx - 6, cy + 1);
    g.lineTo(cx - 15, cy - 2);
    g.strokePath();
    // Wing tip accent
    g.fillStyle(COLOR_ALTAR_GOLD, 0.3);
    g.fillCircle(cx - 21, cy - 15, 1.5);

    // ── Right wing — mirrored ──
    g.fillStyle(COLOR_ALTAR_CELESTIAL, 0.55);
    g.fillTriangle(
      cx + 4, cy - 4,
      cx + 22, cy - 16,
      cx + 18, cy + 2
    );
    g.fillStyle(COLOR_ALTAR_CELESTIAL, 0.4);
    g.fillTriangle(
      cx + 5, cy - 2,
      cx + 20, cy - 10,
      cx + 16, cy + 4
    );
    g.lineStyle(0.6, COLOR_ALTAR_GOLD, 0.45);
    g.beginPath();
    g.moveTo(cx + 6, cy - 3);
    g.lineTo(cx + 19, cy - 12);
    g.strokePath();
    g.beginPath();
    g.moveTo(cx + 6, cy - 1);
    g.lineTo(cx + 17, cy - 7);
    g.strokePath();
    g.beginPath();
    g.moveTo(cx + 6, cy + 1);
    g.lineTo(cx + 15, cy - 2);
    g.strokePath();
    g.fillStyle(COLOR_ALTAR_GOLD, 0.3);
    g.fillCircle(cx + 21, cy - 15, 1.5);

    // ── Base pedestal — wide three-tiered stepped platform ──
    // Bottom tier (widest)
    g.fillStyle(COLOR_ALTAR_GOLD, 0.5);
    g.fillRect(cx - 14, cy + 16, 28, 4);
    // Middle tier
    g.fillStyle(COLOR_ALTAR_GOLD, 0.6);
    g.fillRect(cx - 11, cy + 12, 22, 5);
    // Top tier
    g.fillStyle(COLOR_ALTAR_GOLD, 0.7);
    g.fillRect(cx - 8, cy + 8, 16, 5);
    // Pedestal edge highlights
    g.lineStyle(0.5, 0xffffff, 0.25);
    g.beginPath();
    g.moveTo(cx - 14, cy + 16);
    g.lineTo(cx + 14, cy + 16);
    g.strokePath();
    g.beginPath();
    g.moveTo(cx - 11, cy + 12);
    g.lineTo(cx + 11, cy + 12);
    g.strokePath();

    // ── Central pillar / altar body ──
    g.fillStyle(COLOR_ALTAR_GOLD, 0.8);
    g.fillRect(cx - 5, cy - 6, 10, 15);
    // Pillar grooves (vertical lines for ornate feel)
    g.lineStyle(0.4, 0xffffff, 0.15);
    for (let lx = -3; lx <= 3; lx += 2) {
      g.beginPath();
      g.moveTo(cx + lx, cy - 5);
      g.lineTo(cx + lx, cy + 8);
      g.strokePath();
    }

    // ── Top crown / capital ──
    g.fillStyle(COLOR_ALTAR_GOLD, 0.95);
    g.fillRect(cx - 7, cy - 9, 14, 4);
    // Crown pinnacles — three small spires
    g.fillStyle(COLOR_ALTAR_GOLD, 0.9);
    g.fillTriangle(cx, cy - 16, cx - 2, cy - 9, cx + 2, cy - 9);
    g.fillTriangle(cx - 5, cy - 13, cx - 7, cy - 9, cx - 3, cy - 9);
    g.fillTriangle(cx + 5, cy - 13, cx + 3, cy - 9, cx + 7, cy - 9);
    // Pinnacle tip sparkles
    g.fillStyle(0xffffff, 0.9);
    g.fillCircle(cx, cy - 16, 1.0);
    g.fillStyle(0xffffff, 0.6);
    g.fillCircle(cx - 5, cy - 13, 0.7);
    g.fillCircle(cx + 5, cy - 13, 0.7);

    // ── Central gemstone — large radiant jewel ──
    // Gem outer glow
    g.fillStyle(COLOR_ALTAR_GOLD, 0.25);
    g.fillCircle(cx, cy + 1, 5);
    // Gem body
    g.fillStyle(COLOR_ALTAR_GOLD, 0.7);
    g.fillCircle(cx, cy + 1, 3.5);
    // Gem bright core
    g.fillStyle(0xffffff, 0.95);
    g.fillCircle(cx, cy + 1, 2);
    // Gem highlight
    g.fillStyle(0xffffff, 0.5);
    g.fillCircle(cx - 0.8, cy + 0.2, 0.8);

    // ── Ornamental details — small gems on pedestal ──
    g.fillStyle(COLOR_ALTAR_GOLD, 0.6);
    g.fillCircle(cx - 6, cy + 14, 1.0);
    g.fillCircle(cx + 6, cy + 14, 1.0);
    g.fillStyle(0xffffff, 0.4);
    g.fillCircle(cx - 6, cy + 14, 0.5);
    g.fillCircle(cx + 6, cy + 14, 0.5);

    // ── Celestial rays — subtle lines radiating from gem ──
    g.lineStyle(0.3, COLOR_ALTAR_GOLD, 0.2);
    const rayCount = 8;
    for (let r = 0; r < rayCount; r++) {
      const angle = (r / rayCount) * Math.PI * 2;
      const innerR = 5;
      const outerR = 10;
      g.beginPath();
      g.moveTo(cx + Math.cos(angle) * innerR, cy + 1 + Math.sin(angle) * innerR);
      g.lineTo(cx + Math.cos(angle) * outerR, cy + 1 + Math.sin(angle) * outerR);
      g.strokePath();
    }

    g.generateTexture(TEX_ALTAR_THREIA, s, s);
    g.destroy();
  }

  /**
   * Dark Sphere: Vorath's projectile — dark core with white/red rim.
   * 16px, fired toward the player during the final battle.
   */
  _generateDarkSphereTexture() {
    const s = FINAL_VORATH_SPHERE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = s / 2;
    const cy = s / 2;
    const r = s * 0.4;

    // Outer red glow
    g.fillStyle(COLOR_DARK_SPHERE_RIM, 0.2);
    g.fillCircle(cx, cy, r + 2);

    // Red rim ring
    g.lineStyle(2, COLOR_DARK_SPHERE_RIM, 0.8);
    g.strokeCircle(cx, cy, r);

    // White outer accent
    g.lineStyle(0.8, 0xffffff, 0.35);
    g.strokeCircle(cx, cy, r + 0.8);

    // Dark core fill
    g.fillStyle(COLOR_DARK_SPHERE, 1.0);
    g.fillCircle(cx, cy, r - 1);

    // Inner void
    g.fillStyle(0x000000, 0.8);
    g.fillCircle(cx, cy, r * 0.4);

    // Red energy spark
    g.fillStyle(COLOR_DARK_SPHERE_RIM, 0.7);
    g.fillCircle(cx - 1, cy - 1, 1);

    g.generateTexture(TEX_DARK_SPHERE, s, s);
    g.destroy();
  }

  /**
   * Beam Particle: small light particle used for altar beam effects.
   * 6px, used in particle emitters during the beam phase.
   */
  _generateBeamParticleTexture() {
    const s = 6;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = s / 2;
    const cy = s / 2;

    // Soft golden glow
    g.fillStyle(COLOR_ALTAR_GOLD, 0.3);
    g.fillCircle(cx, cy, 3);

    // Brighter core
    g.fillStyle(COLOR_ALTAR_GOLD, 0.7);
    g.fillCircle(cx, cy, 2);

    // White center
    g.fillStyle(0xffffff, 0.95);
    g.fillCircle(cx, cy, 1);

    g.generateTexture(TEX_BEAM_PARTICLE, s, s);
    g.destroy();
  }

  // ---------------------------------------------------------------
  // Medium-priority feature texture generators
  // ---------------------------------------------------------------

  /**
   * Secret Wall: wall tile with a subtle crack line.
   * Nearly identical to the standard wall but with a faint crack.
   */
  _generateSecretWallTexture() {
    const s = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Standard wall base (white)
    g.fillStyle(COLOR_WHITE, 1.0);
    g.fillRect(0, 0, s, s);

    // Edge lines (same as standard wall)
    g.lineStyle(0.5, 0xcccccc, 0.3);
    g.strokeRect(0.5, 0.5, s - 1, s - 1);

    // Subtle crack line — the only visual hint
    g.lineStyle(0.5, COLOR_SECRET_WALL_CRACK, 0.25);
    g.beginPath();
    g.moveTo(s * 0.3, s * 0.1);
    g.lineTo(s * 0.45, s * 0.35);
    g.lineTo(s * 0.35, s * 0.55);
    g.lineTo(s * 0.5, s * 0.75);
    g.lineTo(s * 0.65, s * 0.9);
    g.strokePath();

    // Very faint secondary crack
    g.lineStyle(0.3, COLOR_SECRET_WALL_CRACK, 0.15);
    g.beginPath();
    g.moveTo(s * 0.45, s * 0.35);
    g.lineTo(s * 0.6, s * 0.4);
    g.strokePath();

    g.generateTexture(TEX_SECRET_WALL, s, s);
    g.destroy();
  }

  /**
   * Shadow Walker: dark hooded phantom silhouette with glowing purple eyes.
   * Larger, more recognizable ghostly figure (~22px).
   * Light outline drawn first so it stands out against the dark floor.
   */
  _generateShadowWalkerTexture() {
    const s = SHADOW_WALKER_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = s / 2;
    const cy = s / 2;

    // ── Outline pass — light border behind fills (same technique as bat) ──
    const outlineColor = 0xaaaaaa;
    const outlineAlpha = 0.7;

    // Hood outline
    g.lineStyle(1.5, outlineColor, outlineAlpha);
    g.strokeCircle(cx, cy - s * 0.15, s * 0.28);

    // Cloak body outline
    g.lineStyle(1.2, outlineColor, outlineAlpha);
    g.strokeEllipse(cx, cy + s * 0.12, s * 0.6, s * 0.65);

    // Arm outlines
    g.lineStyle(1, outlineColor, outlineAlpha * 0.6);
    g.strokeEllipse(cx - s * 0.30, cy + s * 0.05, s * 0.14, s * 0.28);
    g.strokeEllipse(cx + s * 0.30, cy + s * 0.05, s * 0.14, s * 0.28);

    // Tendril outlines
    g.lineStyle(1, outlineColor, outlineAlpha * 0.5);
    g.beginPath();
    g.moveTo(cx - s * 0.22, cy + s * 0.3);
    g.lineTo(cx - s * 0.30, cy + s * 0.48);
    g.lineTo(cx - s * 0.10, cy + s * 0.42);
    g.closePath();
    g.strokePath();
    g.beginPath();
    g.moveTo(cx - s * 0.04, cy + s * 0.32);
    g.lineTo(cx - s * 0.08, cy + s * 0.50);
    g.lineTo(cx + s * 0.08, cy + s * 0.50);
    g.closePath();
    g.strokePath();
    g.beginPath();
    g.moveTo(cx + s * 0.22, cy + s * 0.3);
    g.lineTo(cx + s * 0.10, cy + s * 0.42);
    g.lineTo(cx + s * 0.30, cy + s * 0.48);
    g.closePath();
    g.strokePath();

    // ── Fill pass ──

    // Outer wispy aura (subtle dark halo)
    g.fillStyle(COLOR_SHADOW_WALKER, 0.25);
    g.fillEllipse(cx, cy + 1, s * 0.85, s * 0.95);

    // Main cloak body (tapered hooded robe shape)
    g.fillStyle(COLOR_SHADOW_WALKER, 0.95);
    g.fillEllipse(cx, cy + s * 0.12, s * 0.6, s * 0.65);

    // Hood (larger rounded top)
    g.fillStyle(COLOR_SHADOW_WALKER, 1.0);
    g.fillCircle(cx, cy - s * 0.15, s * 0.28);

    // Inner hood shadow (darker inset for depth)
    g.fillStyle(0x000008, 0.9);
    g.fillCircle(cx, cy - s * 0.12, s * 0.19);

    // Wispy tendrils at bottom (tattered cloak edges)
    g.fillStyle(COLOR_SHADOW_WALKER, 0.5);
    g.fillTriangle(
      cx - s * 0.22, cy + s * 0.3,
      cx - s * 0.30, cy + s * 0.48,
      cx - s * 0.10, cy + s * 0.42
    );
    g.fillTriangle(
      cx - s * 0.04, cy + s * 0.32,
      cx - s * 0.08, cy + s * 0.50,
      cx + s * 0.08, cy + s * 0.50
    );
    g.fillTriangle(
      cx + s * 0.22, cy + s * 0.3,
      cx + s * 0.10, cy + s * 0.42,
      cx + s * 0.30, cy + s * 0.48
    );

    // Faint arm-like extensions on sides
    g.fillStyle(COLOR_SHADOW_WALKER, 0.45);
    g.fillEllipse(cx - s * 0.30, cy + s * 0.05, s * 0.14, s * 0.28);
    g.fillEllipse(cx + s * 0.30, cy + s * 0.05, s * 0.14, s * 0.28);

    // Eyes — glowing purple in the hood shadow
    const eyeY = cy - s * 0.14;
    const eyeSpacing = s * 0.10;

    // Eye outer glow
    g.fillStyle(COLOR_SHADOW_WALKER_EYES, 0.4);
    g.fillCircle(cx - eyeSpacing, eyeY, 2.2);
    g.fillCircle(cx + eyeSpacing, eyeY, 2.2);

    // Eye main
    g.fillStyle(COLOR_SHADOW_WALKER_EYES, 0.9);
    g.fillCircle(cx - eyeSpacing, eyeY, 1.5);
    g.fillCircle(cx + eyeSpacing, eyeY, 1.5);

    // Eye bright core
    g.fillStyle(0xcc44ff, 1.0);
    g.fillCircle(cx - eyeSpacing, eyeY, 0.7);
    g.fillCircle(cx + eyeSpacing, eyeY, 0.7);

    g.generateTexture(TEX_SHADOW_WALKER, s, s);
    g.destroy();
  }

  /**
   * Bat Nest: a small dark cluster with red eyes peeking out.
   * Sits on wall cells, ~20px.
   */
  _generateBatNestTexture() {
    const s = BAT_NEST_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = s / 2;
    const cy = s / 2;

    // Main nest body — irregular dark brown mass
    g.fillStyle(COLOR_BAT_NEST, 0.9);
    g.fillCircle(cx, cy, s * 0.35);
    g.fillCircle(cx - 2, cy - 1, s * 0.25);
    g.fillCircle(cx + 2, cy + 1, s * 0.25);
    g.fillCircle(cx - 1, cy + 2, s * 0.22);
    g.fillCircle(cx + 1, cy - 2, s * 0.2);

    // Twig-like texture lines
    g.lineStyle(0.4, 0x221100, 0.6);
    g.beginPath();
    g.moveTo(cx - 4, cy - 2);
    g.lineTo(cx + 3, cy + 1);
    g.strokePath();
    g.beginPath();
    g.moveTo(cx - 2, cy - 4);
    g.lineTo(cx + 1, cy + 3);
    g.strokePath();
    g.beginPath();
    g.moveTo(cx - 3, cy + 2);
    g.lineTo(cx + 4, cy - 1);
    g.strokePath();

    // Red eyes peeking out (3 pairs for a cluster feel)
    g.fillStyle(COLOR_BAT_NEST_EYES, 0.8);
    // Pair 1 — center
    g.fillCircle(cx - 1.5, cy - 1, 0.8);
    g.fillCircle(cx + 1.5, cy - 1, 0.8);
    // Pair 2 — upper left
    g.fillCircle(cx - 3, cy - 3, 0.6);
    g.fillCircle(cx - 1.5, cy - 3, 0.6);
    // Pair 3 — lower right
    g.fillCircle(cx + 1.5, cy + 2, 0.6);
    g.fillCircle(cx + 3.5, cy + 2, 0.6);

    g.generateTexture(TEX_BAT_NEST, s, s);
    g.destroy();
  }

  /**
   * Bat Nest (Destroyed): scattered debris remnants of a destroyed nest.
   * Same size as the nest but visually broken apart.
   */
  _generateBatNestDestroyedTexture() {
    const s = BAT_NEST_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    const cx = s / 2;
    const cy = s / 2;

    // Scattered debris fragments
    g.fillStyle(COLOR_BAT_NEST, 0.4);
    g.fillCircle(cx - 3, cy + 2, 1.5);
    g.fillCircle(cx + 3, cy - 1, 1.2);
    g.fillCircle(cx, cy + 3, 1.0);
    g.fillCircle(cx - 2, cy - 3, 0.8);
    g.fillCircle(cx + 4, cy + 3, 1.1);

    // Twig fragments
    g.lineStyle(0.3, 0x221100, 0.3);
    g.beginPath();
    g.moveTo(cx - 4, cy);
    g.lineTo(cx - 2, cy + 1);
    g.strokePath();
    g.beginPath();
    g.moveTo(cx + 1, cy - 2);
    g.lineTo(cx + 3, cy + 1);
    g.strokePath();

    g.generateTexture(TEX_BAT_NEST_DESTROYED, s, s);
    g.destroy();
  }

  /**
   * Crumbled Wall: a TILE_SIZE wall that looks partially destroyed/broken,
   * used adjacent to bat nests to convey that the nest has eroded the wall.
   * Shows irregular broken edges, cracks, and missing chunks.
   */
  _generateWallCrumbledTexture() {
    const s = TILE_SIZE;
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Base: partial wall fill (darker gray, not full coverage)
    g.fillStyle(COLOR_BAT_NEST_CRUMBLED, 0.7);
    // Irregular base shape — leave gaps at edges to look broken
    g.fillRect(2, 3, s - 5, s - 6);

    // Lighter rubble chunks scattered around
    g.fillStyle(0x555555, 0.6);
    g.fillRect(0, s - 5, 4, 3);
    g.fillRect(s - 4, 0, 3, 4);
    g.fillRect(1, 0, 5, 2);
    g.fillRect(s - 6, s - 3, 5, 2);

    // Dark cracks across the surface
    g.lineStyle(0.6, 0x222222, 0.7);
    g.beginPath();
    g.moveTo(3, 4);
    g.lineTo(s * 0.4, s * 0.5);
    g.lineTo(s * 0.3, s - 3);
    g.strokePath();
    g.beginPath();
    g.moveTo(s - 3, 5);
    g.lineTo(s * 0.6, s * 0.45);
    g.lineTo(s * 0.7, s - 4);
    g.strokePath();

    // Small debris dots (fallen rubble)
    g.fillStyle(0x666666, 0.5);
    g.fillCircle(2, s - 2, 1.2);
    g.fillCircle(s - 2, 2, 1.0);
    g.fillCircle(s * 0.5, 1, 0.8);
    g.fillCircle(s * 0.5, s - 1, 0.9);

    g.generateTexture(TEX_WALL_CRUMBLED, s, s);
    g.destroy();
  }
}
